import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { toast } from "sonner";
import { Bar, BarChart, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { motion, useReducedMotion } from "framer-motion";
import {
  LayoutDashboard, Boxes, Package, Tags, UserPlus, Wallet, ShoppingCart, BarChart3,
  AlertTriangle, CalendarClock, Bell, Home, LogOut, DollarSign, Users, Store as StoreIcon,
  Plus, Trash2, X, Zap, TrendingUp, TrendingDown, CheckCircle2, Search, ListChecks, UserCog, Send, Settings, Save, Pencil,
  PieChart, Eye, EyeOff, Filter, RotateCcw, Paintbrush, Bot, Clock, XCircle, FileDown, Ticket, History, Copy, PackagePlus, Mail, Gift, KeyRound, PauseCircle,
} from "lucide-react";
import TenantBrand from "@/components/TenantBrand";
import { parseDelivered } from "@/lib/deliveredFormat";
import { placeholderImage } from "@/lib/placeholderImage";
import prettyProductName from "@/lib/prettyProductName";

const copyText = (text) => navigator.clipboard?.writeText(text).then(() => toast.success("Copiado"));
const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => new Date(d).toLocaleString("es-MX", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
// fecha_compra viene de un <input type="date"> como "YYYY-MM-DD" (sin hora) --
// agregarle T00:00:00 evita que se interprete como UTC y se recorra un día
// para quien esté en una zona horaria negativa (México).
const fmtDateOnly = (d) => d ? new Date(`${d}T00:00:00`).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";
const downloadCsv = async (endpoint, filename) => {
  const { data } = await api.get(endpoint, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const MENU = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "stock", label: "Stock", icon: Boxes },
  { id: "stock-auto", label: "Stock Automático", icon: Zap },
  { id: "productos", label: "Productos", icon: Package },
  { id: "etiquetas", label: "Etiquetas", icon: Tags },
  { id: "creditos", label: "Agregar Créditos", icon: UserPlus },
  { id: "venta-manual", label: "Agregar Venta", icon: PackagePlus },
  { id: "venta-masiva", label: "Agregar Venta Masiva", icon: ListChecks },
  { id: "resumen", label: "Resumen Créditos", icon: Wallet },
  { id: "recargas", label: "Recargas Automáticas", icon: Bot },
  { id: "ventas", label: "Ventas", icon: ShoppingCart },
  { id: "estadisticas", label: "Estadisticas", icon: BarChart3 },
  { id: "cupones", label: "Cupones", icon: Ticket },
  { id: "ruleta", label: "Ruleta de Premios", icon: Gift },
  { id: "canva", label: "Invitaciones por Correo", icon: Mail },
  { id: "reportes", label: "Reportes", icon: AlertTriangle },
  { id: "codigos", label: "Códigos de Acceso", icon: KeyRound },
  { id: "vencimientos", label: "Vencimientos", icon: CalendarClock },
  { id: "notificaciones", label: "Notificaciones", icon: Bell },
  { id: "bitacora", label: "Bitácora", icon: History },
  { id: "personalizar", label: "Personaliza tu Tienda", icon: Paintbrush },
];

export default function AdminPanel() {
  const { logout } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [active, setActive] = useState("dashboard");
  const [mobileOpen, setMobileOpen] = useState(false);
  const Section = { dashboard: Dashboard, stock: Stock, "stock-auto": StockAutomatico, productos: Productos, etiquetas: Etiquetas, creditos: Creditos, "venta-manual": VentaManual, "venta-masiva": VentaMasiva, resumen: Resumen, recargas: RecargasAutomaticas, cupones: Cupones, ruleta: Ruleta, canva: CanvaQueue, ventas: Ventas, estadisticas: Estadisticas, reportes: Reportes, codigos: Codigos, vencimientos: Vencimientos, notificaciones: Notificaciones, bitacora: Bitacora, personalizar: Personalizar }[active];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-primary text-white shadow-md">
        <div className="px-4 h-[70px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"} className="lg:hidden p-2"><LayoutDashboard size={20} /></button>
            <TenantBrand tenant={tenant} />
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden md:flex items-center gap-1.5 bg-white/15 rounded-lg px-3 py-2.5 text-sm font-semibold"><UserCog size={16} /> Panel Admin</span>
            <button data-testid="nav-home" onClick={() => navigate("/")} className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors"><Home size={16} /> Inicio</button>
            <button data-testid="nav-logout" onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors"><LogOut size={16} /> Salir</button>
          </div>
        </div>
      </header>

      <div className="flex">
        {mobileOpen && <div className="fixed inset-0 z-40 bg-slate-900/50 lg:hidden" onClick={() => setMobileOpen(false)} />}
        <aside className={`fixed left-0 top-[70px] bottom-0 z-50 overflow-y-auto transition-transform duration-200 ease-out ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 lg:static lg:top-auto lg:bottom-auto lg:z-auto lg:min-h-[calc(100vh-70px)] w-72 shrink-0 bg-slate-100 border-r border-slate-200 p-4`}>
          <p className="text-slate-500 font-semibold px-3 mb-3 border-b border-slate-200 pb-3">Menú de Administración</p>
          <nav className="space-y-1">
            {MENU.map((m) => {
              const Icon = m.icon; const on = active === m.id;
              return (
                <button key={m.id} data-testid={`menu-${m.id}`} aria-current={on ? "page" : undefined} onClick={() => { setActive(m.id); setMobileOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-bold uppercase transition-colors whitespace-nowrap ${on ? "bg-primary text-white shadow" : "text-slate-600 hover:bg-slate-200"}`}>
                  <Icon size={20} className={`shrink-0 ${on ? "text-white" : "text-primary"}`} /> {m.label}
                </button>
              );
            })}
          </nav>
        </aside>
        <main className="flex-1 p-4 sm:p-8 min-w-0"><Section go={setActive} /></main>
      </div>
    </div>
  );
}

/* shared */
function Title({ icon: Icon, children }) {
  return <div className="flex items-center gap-3 mb-6"><Icon size={30} className="text-primary" /><h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900 uppercase">{children}</h1></div>;
}
function Card({ children, className = "" }) { return <div className={`bg-white rounded-2xl border border-slate-200 ${className}`}>{children}</div>; }
function CardHead({ icon: Icon, children }) { return <div className="flex items-center gap-2 pb-4 mb-5 border-b border-slate-100"><Icon size={22} className="text-primary" /><h2 className="font-head text-xl font-bold text-slate-900">{children}</h2></div>; }
function Field({ label, children }) { return <label className="block mb-4"><span className="text-sm font-semibold text-slate-700 mb-1.5 block">{label}</span>{children}</label>; }
function inputCls() { return "w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white transition-colors"; }
function BigBtn({ children, onClick, testid, disabled }) { return <button data-testid={testid} onClick={onClick} disabled={disabled} className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-white font-bold rounded-lg py-3.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">{children}</button>; }
function Loading() { return <p className="text-slate-400 py-10">Cargando...</p>; }
// Credenciales de cuentas reales (stock, ventas, reportes) ocultas por
// defecto -- quedaban en texto plano permanente en pantalla, visibles en
// cualquier captura o mirada de reojo. Un clic las revela cuando de verdad
// se necesitan.
function Secret({ value, placeholder = "•••••••••" }) {
  const [shown, setShown] = useState(false);
  if (!value) return <span className="text-slate-300">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={shown ? "break-all" : ""}>{shown ? value : placeholder}</span>
      <button type="button" onClick={() => setShown((s) => !s)} aria-label={shown ? "Ocultar" : "Mostrar"} className="shrink-0 text-slate-400 hover:text-primary">
        {shown ? <EyeOff size={13} /> : <Eye size={13} />}
      </button>
    </span>
  );
}
function useEscapeClose(isOpen, onClose) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);
}
function ConfirmDialog({ title, message, confirmLabel = "Eliminar", busyLabel = "Eliminando...", busy, onConfirm, onCancel, children }) {
  useEscapeClose(true, onCancel);
  return (
    <div role="dialog" aria-modal="true" aria-label={title} className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-sm p-6 animate-fade-up shadow-md">
        <h3 className="font-head text-lg font-extrabold text-slate-900">{title}</h3>
        <p className="text-sm text-slate-500 mt-2">{message}</p>
        {children}
        <div className="flex gap-3 mt-5">
          <button onClick={onCancel} className="flex-1 border border-slate-300 hover:bg-slate-50 text-slate-700 font-bold rounded-lg py-2.5 transition-colors">Cancelar</button>
          <button onClick={onConfirm} disabled={busy} className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-bold rounded-lg py-2.5 transition-colors">{busy ? busyLabel : confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

/* Personaliza tu Tienda */
const COLOR_PRESETS = [
  { label: "Rojo Mercado", value: "#DC2626" },
  { label: "Negro Autoridad", value: "#0F172A" },
  { label: "Turquesa + Blanco", value: "#2CBFDD" },
  { label: "Azul Océano", value: "#2563EB" },
  { label: "Verde Esmeralda", value: "#059669" },
  { label: "Morado Real", value: "#7C3AED" },
  { label: "Naranja Vibrante", value: "#EA580C" },
  { label: "Rosa Fuerte", value: "#DB2777" },
];
const MAX_LOGO_BYTES = 10 * 1024 * 1024;

function Personalizar() {
  const { tenant, refresh } = useTenant();
  const [form, setForm] = useState({ logo_url: "", primary_color: "#DC2626", codigos_pausados: false, codigos_pausados_mensaje: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm({
      logo_url: tenant.logo_url, primary_color: tenant.primary_color,
      codigos_pausados: !!tenant.codigos_pausados, codigos_pausados_mensaje: tenant.codigos_pausados_mensaje || "",
    });
  }, [tenant]);

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      return toast.error("Solo se aceptan imágenes PNG, JPG o WEBP");
    }
    if (file.size > MAX_LOGO_BYTES) {
      return toast.error("La imagen debe pesar menos de 10 MB");
    }
    const fd = new FormData(); fd.append("file", file);
    setUploadingLogo(true);
    try {
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: data.url }));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploadingLogo(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/tenant/config", {
        name: tenant.name, logo_url: form.logo_url, primary_color: form.primary_color,
        codigos_pausados: form.codigos_pausados, codigos_pausados_mensaje: form.codigos_pausados_mensaje,
      });
      await refresh();
      toast.success("Tu tienda se actualizó");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <Title icon={Paintbrush}>Personaliza tu Tienda</Title>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <CardHead icon={Save}>Marca de tu tienda</CardHead>
          <Field label="Logo (PNG, JPG o WEBP, máx. 10 MB):">
            <div className="flex items-center gap-3">
              {form.logo_url && <img src={form.logo_url} alt="Logo actual" className="h-11 w-11 object-contain rounded-lg border border-slate-200 bg-white p-1" />}
              <label className={`flex-1 flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-lg py-3 text-sm font-semibold text-slate-600 transition-colors ${uploadingLogo ? "opacity-50" : "hover:border-primary hover:text-primary cursor-pointer"}`}>
                <input type="file" accept="image/png, image/jpeg, image/webp" onChange={onLogoFile} disabled={uploadingLogo} className="hidden" />
                {uploadingLogo ? "Subiendo..." : "Subir imagen"}
              </label>
              <button type="button" onClick={() => setForm((f) => ({ ...f, logo_url: "" }))} disabled={!form.logo_url}
                className="shrink-0 border border-slate-200 hover:border-primary hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed text-slate-600 text-sm font-semibold rounded-lg px-4 py-3 transition-colors">
                Sin imagen
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-1.5">Sin imagen: en vez del logo se muestra el nombre de tu tienda ("{tenant.name}") en la barra de navegación.</p>
          </Field>
          <Field label="Color principal:">
            <div className="grid grid-cols-4 gap-2 mb-3">
              {COLOR_PRESETS.map((p) => (
                <button key={p.value} type="button" onClick={() => setForm({ ...form, primary_color: p.value })} title={p.label}
                  className={`h-12 rounded-lg border-2 transition-all ${form.primary_color?.toLowerCase() === p.value.toLowerCase() ? "border-slate-900 scale-95" : "border-transparent hover:scale-95"}`}
                  style={{ background: p.value }} />
              ))}
            </div>
            <div className="flex items-center gap-3">
              <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                className="w-14 h-11 rounded-lg border border-slate-200 cursor-pointer" />
              <input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                className={inputCls() + " flex-1"} placeholder="#DC2626" />
            </div>
          </Field>
          <BigBtn testid="personalizar-save" onClick={save}>{saving ? "Guardando..." : <><Save size={18} /> Guardar cambios</>}</BigBtn>
        </Card>
        <Card className="p-6">
          <CardHead icon={PauseCircle}>Sección "Pedir código"</CardHead>
          <label className="flex items-center gap-3 mb-4 cursor-pointer">
            <input type="checkbox" checked={form.codigos_pausados} onChange={(e) => setForm((f) => ({ ...f, codigos_pausados: e.target.checked }))} className="w-5 h-5 accent-primary" />
            <span className="text-sm font-semibold text-slate-700">Pausar la sección de códigos (Netflix, Disney+, Fox One, Apple TV, etc.)</span>
          </label>
          <Field label="Mensaje para el cliente mientras está pausada (opcional):">
            <textarea value={form.codigos_pausados_mensaje} onChange={(e) => setForm((f) => ({ ...f, codigos_pausados_mensaje: e.target.value }))}
              rows={3} placeholder="La sección de códigos está pausada por ahora -- vuelve a intentarlo más tarde." className={inputCls()} />
          </Field>
          <p className="text-xs text-slate-400">Mientras esté pausada, tus clientes no podrán pedir códigos nuevos desde el panel.</p>
          <div className="mt-4"><BigBtn testid="personalizar-codigos-save" onClick={save}>{saving ? "Guardando..." : <><Save size={18} /> Guardar cambios</>}</BigBtn></div>
        </Card>
        <Card className="p-6">
          <CardHead icon={Eye}>Vista previa</CardHead>
          <div className="rounded-xl overflow-hidden border border-slate-200">
            <div className="h-16 flex items-center px-4 gap-3" style={{ background: form.primary_color }}>
              {form.logo_url
                ? <img src={form.logo_url} alt="Logo" className="h-8 w-auto object-contain" />
                : <span className="font-head text-base font-extrabold uppercase tracking-tight text-white">{tenant.name}</span>}
            </div>
            <div className="p-4 bg-white">
              <button className="text-white font-semibold rounded-lg px-4 py-2.5 text-sm" style={{ background: form.primary_color }}>+ Añadir</button>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-3">Este color y logo se aplican al instante en toda tu tienda al guardar — barra de navegación, botones, y las gráficas de Estadísticas.</p>
        </Card>
      </div>
    </div>
  );
}

/* Dashboard */
function Reveal({ children, delay = 0, className = "" }) {
  const reduced = useReducedMotion();
  return (
    <motion.div className={className}
      initial={reduced ? false : { opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduced ? 0 : 0.4, delay: reduced ? 0 : delay, ease: [0.16, 1, 0.3, 1] }}>
      {children}
    </motion.div>
  );
}
function ReportAlertTile({ go, icon: Icon, label, value, activeCls, textCls, cta }) {
  const on = value > 0;
  return (
    <button onClick={() => on && go("reportes")} className={`w-full h-full text-left rounded-2xl p-6 transition-colors ${on ? `${activeCls} cursor-pointer` : "bg-white border border-slate-200"}`}>
      <div className={`flex items-center gap-2 mb-1 ${on ? textCls : "text-slate-500"}`}><Icon size={16} /><p className="text-xs font-semibold uppercase tracking-wide">{label}</p></div>
      <p className={`font-head text-5xl font-extrabold leading-none ${on ? "text-white" : "text-slate-900"}`}>{value}</p>
      {on ? <p className={`text-xs font-semibold mt-2 ${textCls}`}>{cta}</p> : <p className="text-xs text-slate-400 mt-2">Ninguno</p>}
    </button>
  );
}
function MiniStat({ icon: Icon, label, value }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-slate-500 mb-1"><Icon size={14} className="text-primary" /><p className="text-[11px] font-semibold uppercase tracking-wide">{label}</p></div>
      <p className="font-head text-xl font-extrabold text-slate-900">{value}</p>
    </Card>
  );
}
function Dashboard({ go }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <Loading />;
  const secondary = [
    { icon: ShoppingCart, label: "Ventas del Mes", value: d.ventas_mes },
    { icon: Users, label: "Clientes Activos", value: d.clientes_activos },
    { icon: Users, label: "Total Usuarios", value: d.total_usuarios },
    { icon: StoreIcon, label: "Servicios", value: d.servicios },
  ];
  return (
    <div>
      <Title icon={LayoutDashboard}>Dashboard</Title>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
        <Reveal className="lg:col-span-2">
          <Card className="p-6 h-full flex flex-col justify-center">
            <div className="flex items-center gap-2 text-slate-500 mb-1"><DollarSign size={16} className="text-primary" /><p className="text-xs font-semibold uppercase tracking-wide">Ganancias del Mes</p></div>
            <p className="font-head text-5xl font-extrabold text-primary leading-none">{money(d.ganancias_mes)}</p>
            <p className="text-xs text-slate-400 mt-2 capitalize">{new Date().toLocaleDateString("es-MX", { month: "long", year: "numeric" })}</p>
          </Card>
        </Reveal>
        <Reveal delay={0.08}>
          <ReportAlertTile go={go} icon={AlertTriangle} label="Reportes Pendientes" value={d.reportes_pendientes}
            activeCls="bg-primary hover:bg-primary-hover" textCls="text-white/80" cta="Requieren atención →" />
        </Reveal>
        <Reveal delay={0.12}>
          <ReportAlertTile go={go} icon={Send} label="Reportes en Proceso" value={d.reportes_en_proceso}
            activeCls="bg-blue-600 hover:bg-blue-700" textCls="text-white/80" cta="En seguimiento →" />
        </Reveal>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {secondary.map((s, i) => <Reveal key={s.label} delay={0.12 + i * 0.04}><MiniStat {...s} /></Reveal>)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-2">
        <Reveal delay={0.2} className="lg:col-span-2">
          <Card className="p-6 h-full">
            <h3 className="font-head font-bold text-slate-900 flex items-center gap-2 mb-4"><TrendingUp size={18} className="text-primary" /> Últimos 7 días</h3>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-2">Ingresos por día</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={d.chart} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <CartesianGrid stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#cbd5e1" }} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={40} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}K` : v)} />
                    <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                    <Bar dataKey="ingresos" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={24} name="Ingresos" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-2">Ventas por día</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={d.chart} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <CartesianGrid stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#cbd5e1" }} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={30} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                    <Bar dataKey="ventas" fill="#059669" radius={[4, 4, 0, 0]} maxBarSize={24} name="Ventas" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>
        </Reveal>
        <Reveal delay={0.26}>
          <Card className="bg-slate-950 border-slate-800 p-6 text-white h-full">
            <h3 className="font-head font-bold flex items-center gap-2 mb-4"><Bell size={18} className="text-red-500" /> Ventas Recientes</h3>
            <div className="space-y-2">{d.recientes.map((r, i) => <div key={i} className="bg-slate-900 rounded-lg px-3 py-2.5"><p className="font-semibold text-sm truncate">{r.product}</p><p className="text-xs text-slate-400">{r.user} · {fmtDate(r.date)}</p></div>)}</div>
          </Card>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Reveal delay={0.3}>
          <Card className="bg-slate-950 border-slate-800 p-6 text-white h-full">
            <h3 className="font-head font-bold flex items-center gap-2 mb-4"><AlertTriangle size={18} className="text-red-500" /> Stock Bajo</h3>
            <div className="space-y-2">{d.stock_bajo.map((s, i) => <div key={i} className="flex items-center justify-between bg-slate-900 rounded-lg px-3 py-2.5"><span className="text-sm font-semibold truncate">{s.name}</span><span className={`text-xs font-bold px-2 py-0.5 rounded ${s.stock === 0 ? "bg-primary" : "bg-amber-500"}`}>{s.stock} uds</span></div>)}</div>
            <button onClick={() => go("stock")} className="text-xs text-slate-400 hover:text-white underline mt-3">Ir a gestion de stock</button>
          </Card>
        </Reveal>
        <Reveal delay={0.34}>
          <Card className="p-6 h-full">
            <h3 className="font-head font-bold text-slate-900 flex items-center gap-2 mb-4"><Zap size={18} className="text-primary" /> Accesos Rapidos</h3>
            <div className="grid grid-cols-2 gap-3">{[["stock", Boxes, "Stock"], ["productos", Package, "Servicios"], ["ventas", ShoppingCart, "Ventas"], ["reportes", AlertTriangle, "Reportes"], ["estadisticas", BarChart3, "Estadisticas"]].map(([id, Icon, label]) => <button key={id} onClick={() => go(id)} className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 rounded-lg px-4 py-3 text-sm font-semibold text-slate-700 transition-colors"><Icon size={16} className="text-primary" /> {label}</button>)}</div>
          </Card>
        </Reveal>
      </div>
    </div>
  );
}

/* Stock */
function Stock() {
  const [items, setItems] = useState([]);
  const [pid, setPid] = useState("");
  const [months, setMonths] = useState("1");
  const [txt, setTxt] = useState("");
  const [editing, setEditing] = useState(null); // { id, name, accounts: [...] }
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/admin/products").then((r) => { setItems(r.data); if (!pid && r.data[0]) setPid(r.data[0].id); });
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (adding) return; // evita doble-clic duplicando el stock recién agregado
    const lines = txt.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    if (!pid || !lines.length) return toast.error("Selecciona servicio y agrega cuentas");
    setAdding(true);
    try {
      await api.post(`/admin/products/${pid}/stock`, { items: lines, duration_days: Number(months) * 30 });
      toast.success(`${lines.length} cuentas agregadas`); setTxt(""); load();
    } finally {
      setAdding(false);
    }
  };
  const durationLabel = (days) => {
    if (!days) return "-";
    if (days % 365 === 0 && days >= 365) { const y = days / 365; return `${y} ${y === 1 ? "año" : "años"}`; }
    const m = Math.round(days / 30) || 1;
    return `${m} ${m === 1 ? "mes" : "meses"}`;
  };
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  useEscapeClose(!!editing, () => setEditing(null));
  const editStock = (p) => setEditing({ id: p.id, name: p.name, accounts: [...(p.stock_items || [])] });
  const removeAccount = (idx) => setEditing((e) => ({ ...e, accounts: e.accounts.filter((_, i) => i !== idx) }));
  const removeAllAccounts = () => { setEditing((e) => ({ ...e, accounts: [] })); setConfirmClearAll(false); };
  // Agrupa "correo:contra perfil N" por correo, para poder borrar de un jalón
  // todos los perfiles restantes de la misma cuenta en vez de uno por uno.
  // Sin correo detectable (texto libre), cada línea queda en su propio grupo.
  const groupKeyOf = (a) => parseDelivered(a).correo?.toLowerCase() || a;
  const removeGroup = (key) => setEditing((e) => ({ ...e, accounts: e.accounts.filter((a) => groupKeyOf(a) !== key) }));
  const saveEdit = async () => {
    const original = items.find((p) => p.id === editing.id)?.stock_items || [];
    const removed = [...original];
    editing.accounts.forEach((kept) => { const i = removed.indexOf(kept); if (i >= 0) removed.splice(i, 1); });
    if (removed.length === 0) { setEditing(null); return; }
    setSaving(true);
    try {
      for (const item of removed) await api.post(`/admin/products/${editing.id}/stock/remove`, { item });
      toast.success(`${removed.length} cuenta${removed.length === 1 ? "" : "s"} eliminada${removed.length === 1 ? "" : "s"} del stock`);
      setEditing(null); load();
    } finally { setSaving(false); }
  };
  return (
    <div>
      <Title icon={Boxes}>Stock</Title>
      <Card className="p-6 mb-6">
        <CardHead icon={Plus}>Agregar Stock</CardHead>
        <Field label="Servicio:"><select data-testid="stock-service" value={pid} onChange={(e) => setPid(e.target.value)} className={inputCls()}>{items.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
        <Field label="Duración (meses):"><select data-testid="stock-duration" value={months} onChange={(e) => setMonths(e.target.value)} className={inputCls()}>{[1, 3, 6, 12, 24].map((m) => <option key={m} value={m}>{m} {m === 1 ? "mes" : "meses"}</option>)}</select></Field>
        <Field label="Cuentas (separadas por comas):"><textarea data-testid="stock-textarea" value={txt} onChange={(e) => setTxt(e.target.value)} rows={5} placeholder="cuenta1@email.com:password1, cuenta2@email.com:password2" className={inputCls()} /></Field>
        <BigBtn testid="stock-save" onClick={add} disabled={adding}>{adding ? "Agregando..." : <><Plus size={18} /> Agregar Stock</>}</BigBtn>
      </Card>
      <Card className="overflow-x-auto">
        <div className="p-5 pb-0"><CardHead icon={ListChecks}>Stock Disponible</CardHead></div>
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr><th className="text-left px-5 py-3">Servicio</th><th className="text-left px-5 py-3">Duración</th><th className="text-left px-5 py-3">Cantidad</th><th className="text-left px-5 py-3">Cuentas</th><th className="text-right px-5 py-3">Acciones</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((p) => (
              <tr key={p.id} className="hover:bg-slate-50 align-top">
                <td className="px-5 py-3 font-semibold text-slate-900 whitespace-nowrap">{p.name}</td>
                <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{durationLabel(p.duration_days)}</td>
                <td className="px-5 py-3 whitespace-nowrap"><span className={`text-xs font-bold px-2 py-0.5 rounded ${p.stock === 0 ? "bg-red-100 text-red-700" : p.stock < 5 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{p.stock}</span></td>
                <td className="px-5 py-3 text-slate-500 text-xs max-w-md">
                  <Secret value={(p.stock_items || []).join(", ")} placeholder={`•••• (${(p.stock_items || []).length} cuenta${(p.stock_items || []).length === 1 ? "" : "s"})`} />
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap"><button data-testid={`edit-stock-${p.id}`} onClick={() => editStock(p)} className="inline-flex items-center gap-1 bg-primary hover:bg-primary-hover text-white text-xs font-bold rounded-md px-3 py-2 transition-colors"><Pencil size={12} /> Editar</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {editing && (
        <div role="dialog" aria-modal="true" aria-label="Editar Stock" className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden animate-fade-up shadow-md">
            <div className="flex items-center justify-between px-6 py-4 bg-primary text-white shrink-0">
              <div>
                <h3 className="font-head text-lg font-extrabold flex items-center gap-2"><Pencil size={17} /> Editar Stock</h3>
                <p className="text-white/80 text-xs mt-0.5">{editing.name}</p>
              </div>
              <button onClick={() => setEditing(null)} aria-label="Cerrar" className="text-white/80 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="text-xs text-slate-500">Elimina las cuentas que se agregaron por error y guarda para quitarlas del stock. Las que dejes aquí no se tocan.</p>
                {editing.accounts.length > 0 && (
                  <button onClick={() => setConfirmClearAll(true)} className="shrink-0 text-xs font-bold text-red-600 hover:text-red-700 whitespace-nowrap">Eliminar todo</button>
                )}
              </div>
              {editing.accounts.length === 0 ? (
                <p className="text-slate-400 text-center py-8">Sin cuentas restantes.</p>
              ) : (
                <div className="space-y-3">
                  {Array.from(
                    editing.accounts.reduce((map, a, i) => {
                      const key = groupKeyOf(a);
                      if (!map.has(key)) map.set(key, []);
                      map.get(key).push({ a, i });
                      return map;
                    }, new Map())
                  ).map(([key, entries]) => (
                    <div key={key}>
                      {entries.length > 1 && (
                        <div className="flex items-center justify-between mb-1.5 px-1">
                          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{entries.length} perfiles de la misma cuenta</span>
                          <button onClick={() => removeGroup(key)} className="text-xs font-semibold text-red-600 hover:text-red-700">Eliminar los {entries.length}</button>
                        </div>
                      )}
                      <div className="space-y-2">
                        {entries.map(({ a, i }) => (
                          <div key={`${a}-${i}`} className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
                            <span className="text-sm text-slate-700 break-all">{a}</span>
                            <button onClick={() => removeAccount(i)} aria-label="Eliminar cuenta" className="shrink-0 w-7 h-7 grid place-items-center rounded-md text-red-600 hover:bg-red-100 transition-colors"><Trash2 size={15} /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-100 shrink-0">
              <BigBtn onClick={saveEdit}>{saving ? "Actualizando..." : <><Save size={18} /> Actualizar</>}</BigBtn>
            </div>
          </div>
        </div>
      )}
      {confirmClearAll && (
        <ConfirmDialog title="¿Eliminar todas las cuentas?" confirmLabel="Eliminar todo"
          message={`Las ${editing?.accounts.length} cuentas restantes de "${editing?.name}" se quitarán del stock en cuanto le des "Actualizar". Esta acción no se puede deshacer.`}
          onConfirm={removeAllAccounts} onCancel={() => setConfirmClearAll(false)} />
      )}
    </div>
  );
}

/* Stock Automático */
let cuentaSeq = 0;
const emptyCuenta = () => ({ _id: ++cuentaSeq, modo: "cuenta", correo: "", password: "", perfiles: "5", desde: "1", texto: "", lote: "" });

const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const LIST_MARKER_RE = /^\(?\d+[.)]?$/; // folios de lista: "66.", "67)", "1."
// Extrae pares correo/contraseña de un bloque pegado con formato libre: ignora
// líneas de título ("*LOTE VIX*"), líneas en blanco, y cualquier folio numérico
// (con tab, espacio o punto) antes del correo -- el correo se identifica por su
// propio patrón, no por su posición en la línea. Si algunos correos no traen
// contraseña propia en su línea (solo aparece una vez, suelta, en todo el
// bloque), se asume que esa es la contraseña compartida de todos ellos.
function parseLoteCuentas(text) {
  const emailLines = [];
  const passwordCandidates = [];
  for (const raw of (text || "").split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const tokens = line.split(/\s+/).filter((t) => t && !LIST_MARKER_RE.test(t));
    const emailIdx = tokens.findIndex((t) => EMAIL_RE.test(t));
    if (emailIdx !== -1) {
      const email = tokens[emailIdx].match(EMAIL_RE)[0];
      const resto = tokens.filter((_, i) => i !== emailIdx);
      emailLines.push({ email, password: resto[0] || null });
    } else if (tokens.length === 1 && !line.includes("*")) {
      // Línea suelta de un solo token, sin correo y sin pinta de título
      // decorativo -- candidata a ser la contraseña compartida del lote.
      passwordCandidates.push(tokens[0]);
    }
  }
  const fallback = passwordCandidates.length === 1 ? passwordCandidates[0] : null;
  const resolved = emailLines.map((e) => ({ email: e.email, password: e.password || fallback }));
  return {
    pares: resolved.filter((e) => e.password),
    // Correos sí detectados pero sin contraseña resolvible (ni propia en la
    // línea, ni una única suelta de respaldo) -- se quedan fuera del lote en
    // silencio si no se avisan aquí.
    descartados: resolved.filter((e) => !e.password).map((e) => e.email),
  };
}

function StockAutomatico() {
  const [items, setItems] = useState([]);
  const [pid, setPid] = useState("");
  const [months, setMonths] = useState("1");
  const [cuentas, setCuentas] = useState([emptyCuenta()]);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/products").then((r) => { setItems(r.data); if (!pid && r.data[0]) setPid(r.data[0].id); });
  useEffect(() => { load(); }, []);

  const updateCuenta = (id, field, value) => setCuentas((cs) => cs.map((c) => c._id === id ? { ...c, [field]: value } : c));
  const addCuenta = () => setCuentas((cs) => [...cs, emptyCuenta()]);
  const removeCuenta = (id) => setCuentas((cs) => cs.length > 1 ? cs.filter((c) => c._id !== id) : cs);

  // Una cuenta COMPLETA se entrega entera a un solo cliente -- no tiene perfiles,
  // así que el formato es solo correo:contraseña (sin "perfil N") y cada cuenta
  // cuenta como una sola unidad de stock, nunca varias copias de la misma cuenta.
  const selectedProduct = items.find((p) => p.id === pid);
  const isCompleta = /completa/i.test(selectedProduct?.name || "");

  const cuentasValidas = cuentas.filter((c) => {
    const n = isCompleta || Number(c.perfiles) > 0;
    if (c.modo === "texto") return c.texto.trim() && Number(c.perfiles) > 0;
    if (c.modo === "lote") return n && parseLoteCuentas(c.lote).pares.length > 0;
    return c.correo.trim() && c.password.trim() && n;
  });
  const lines = cuentasValidas.flatMap((c) => {
    const n = Number(c.perfiles);
    const start = Number(c.desde) || 1;
    if (c.modo === "texto") return Array.from({ length: n }, () => c.texto.trim());
    if (c.modo === "lote") {
      const { pares } = parseLoteCuentas(c.lote);
      if (isCompleta) return pares.map(({ email, password }) => `${email}:${password}`);
      return pares.flatMap(({ email, password }) =>
        Array.from({ length: n }, (_, i) => `${email}:${password} perfil ${start + i}`));
    }
    if (isCompleta) return [`${c.correo.trim()}:${c.password.trim()}`];
    return Array.from({ length: n }, (_, i) => `${c.correo.trim()}:${c.password.trim()} perfil ${start + i}`);
  });

  const submit = async () => {
    if (saving) return; // evita doble-clic duplicando el stock recién agregado
    if (!pid) return toast.error("Selecciona una plataforma");
    if (cuentasValidas.length === 0) return toast.error("Completa los datos de al menos un bloque");
    setSaving(true);
    try {
      await api.post(`/admin/products/${pid}/stock`, { items: lines, duration_days: Number(months) * 30 });
      toast.success(`${lines.length} unidades agregadas al stock (${cuentasValidas.length} bloque${cuentasValidas.length === 1 ? "" : "s"})`);
      setCuentas([emptyCuenta()]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <Title icon={Zap}>Stock Automático</Title>
      <Card className="p-6">
        <CardHead icon={Zap}>Subir Stock Automático</CardHead>
        <p className="text-xs text-slate-500 mb-4">Genera varias líneas de stock de un jalón. "Cuenta por perfil" arma <span className="font-mono">correo:contraseña perfil N</span> (Netflix, HBO, etc) para una sola cuenta; "Pegar lote" hace lo mismo pero para muchas cuentas a la vez, pegando la lista completa (detecta el correo y la contraseña de cada línea aunque tengan folios o títulos mezclados); "Cuenta por texto" repite el mismo texto N veces (actas, trámites, o cualquier servicio que no sea correo/contraseña — ej. "manda curp"). Puedes mezclar varios bloques, todos para la misma plataforma.</p>
        <Field label="Plataforma:">
          <select data-testid="stockauto-service" value={pid} onChange={(e) => setPid(e.target.value)} className={inputCls()}>
            {items.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Duración (meses):">
          <select value={months} onChange={(e) => setMonths(e.target.value)} className={inputCls()}>{[1, 3, 6, 12, 24].map((m) => <option key={m} value={m}>{m} {m === 1 ? "mes" : "meses"}</option>)}</select>
        </Field>

        <p className="text-sm font-semibold text-slate-700 mb-2">Bloques a subir:</p>
        <div className="space-y-3 mb-4">
          {cuentas.map((c, idx) => (
            <div key={c._id} className="border border-slate-200 rounded-lg p-4 relative">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">Bloque {idx + 1}</p>
              {cuentas.length > 1 && (
                <button onClick={() => removeCuenta(c._id)} aria-label={`Quitar bloque ${idx + 1}`}
                  className="absolute top-3 right-3 w-7 h-7 grid place-items-center rounded-md text-red-600 hover:bg-red-50 transition-colors"><Trash2 size={14} /></button>
              )}
              <div className="flex gap-2 mb-3">
                <button onClick={() => updateCuenta(c._id, "modo", "cuenta")}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${c.modo === "cuenta" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary hover:text-primary"}`}>
                  Cuenta por perfil
                </button>
                <button onClick={() => updateCuenta(c._id, "modo", "texto")}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${c.modo === "texto" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary hover:text-primary"}`}>
                  Cuenta por texto
                </button>
                <button onClick={() => updateCuenta(c._id, "modo", "lote")}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${c.modo === "lote" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary hover:text-primary"}`}>
                  Pegar lote
                </button>
              </div>

              {c.modo === "texto" ? (
                <>
                  <label className="block mb-3">
                    <span className="text-xs font-semibold text-slate-600 mb-1 block">Texto a repetir:</span>
                    <input data-testid={`stockauto-texto-${idx}`} value={c.texto} onChange={(e) => updateCuenta(c._id, "texto", e.target.value)} placeholder="manda curp" className={inputCls()} />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-600 mb-1 block">Cuántas unidades:</span>
                    <input data-testid={`stockauto-perfiles-${idx}`} type="number" min="1" value={c.perfiles} onChange={(e) => updateCuenta(c._id, "perfiles", e.target.value)} className={inputCls()} />
                  </label>
                </>
              ) : c.modo === "lote" ? (
                <>
                  <label className="block mb-3">
                    <span className="text-xs font-semibold text-slate-600 mb-1 block">Pega el lote (una cuenta por línea, correo y contraseña separados por espacio o tab):</span>
                    <textarea data-testid={`stockauto-lote-${idx}`} value={c.lote} onChange={(e) => updateCuenta(c._id, "lote", e.target.value)} rows={8}
                      placeholder={"*LOTE VIX*\ncorreo1@dominio.com\tclave123\ncorreo2@dominio.com\tclave123"}
                      className={`${inputCls()} font-mono text-xs`} />
                    <span className="text-xs text-slate-400 mt-1 block">
                      Ignora automáticamente títulos, líneas en blanco y cualquier número de folio antes del correo. Si los correos no traen su propia contraseña y hay una suelta en el texto (ej. al final), se usa esa misma para todos.
                      {c.lote.trim() && ` ${parseLoteCuentas(c.lote).pares.length} cuenta${parseLoteCuentas(c.lote).pares.length === 1 ? "" : "s"} detectada${parseLoteCuentas(c.lote).pares.length === 1 ? "" : "s"}.`}
                    </span>
                    {c.lote.trim() && parseLoteCuentas(c.lote).descartados.length > 0 && (
                      <p className="text-xs text-red-600 font-semibold mt-1.5 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        ⚠️ {parseLoteCuentas(c.lote).descartados.length} correo{parseLoteCuentas(c.lote).descartados.length === 1 ? "" : "s"} sin contraseña detectada, NO se va{parseLoteCuentas(c.lote).descartados.length === 1 ? "" : "n"} a subir: {parseLoteCuentas(c.lote).descartados.join(", ")}
                      </p>
                    )}
                  </label>
                  {isCompleta ? (
                    <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">Es una cuenta <b>completa</b>: cada correo detectado se sube como <span className="font-mono">correo:contraseña</span>, una sola unidad por cuenta (sin número de perfil).</p>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-3">
                      <label className="block">
                        <span className="text-xs font-semibold text-slate-600 mb-1 block">Perfiles por cuenta:</span>
                        <input data-testid={`stockauto-perfiles-${idx}`} type="number" min="1" value={c.perfiles} onChange={(e) => updateCuenta(c._id, "perfiles", e.target.value)} className={inputCls()} />
                      </label>
                      <label className="block">
                        <span className="text-xs font-semibold text-slate-600 mb-1 block">Empezar en perfil #:</span>
                        <input data-testid={`stockauto-desde-${idx}`} type="number" min="1" value={c.desde} onChange={(e) => updateCuenta(c._id, "desde", e.target.value)} className={inputCls()} />
                      </label>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-xs font-semibold text-slate-600 mb-1 block">Correo de la cuenta:</span>
                      <input data-testid={`stockauto-correo-${idx}`} value={c.correo} onChange={(e) => updateCuenta(c._id, "correo", e.target.value)} placeholder="cuenta@email.com" className={inputCls()} />
                    </label>
                    <label className="block">
                      <span className="text-xs font-semibold text-slate-600 mb-1 block">Contraseña:</span>
                      <input data-testid={`stockauto-password-${idx}`} value={c.password} onChange={(e) => updateCuenta(c._id, "password", e.target.value)} placeholder="Contraseña" className={inputCls()} />
                    </label>
                  </div>
                  {isCompleta ? (
                    <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 mt-3">Es una cuenta <b>completa</b>: se sube como <span className="font-mono">correo:contraseña</span>, una sola unidad (sin número de perfil).</p>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-3 mt-3">
                      <label className="block">
                        <span className="text-xs font-semibold text-slate-600 mb-1 block">Cuántos perfiles:</span>
                        <input data-testid={`stockauto-perfiles-${idx}`} type="number" min="1" value={c.perfiles} onChange={(e) => updateCuenta(c._id, "perfiles", e.target.value)} className={inputCls()} />
                      </label>
                      <label className="block">
                        <span className="text-xs font-semibold text-slate-600 mb-1 block">Empezar en perfil #:</span>
                        <input data-testid={`stockauto-desde-${idx}`} type="number" min="1" value={c.desde} onChange={(e) => updateCuenta(c._id, "desde", e.target.value)} className={inputCls()} />
                      </label>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        <button onClick={addCuenta} className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 hover:border-primary hover:text-primary text-slate-500 font-semibold rounded-lg py-3 mb-5 transition-colors">
          <Plus size={16} /> Agregar otro bloque
        </button>

        {lines.length > 0 && (
          <div className="mb-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Vista previa ({lines.length} unidades de {cuentasValidas.length} bloque{cuentasValidas.length === 1 ? "" : "s"}):</p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 max-h-40 overflow-y-auto text-xs text-slate-600 font-mono space-y-0.5">
              {lines.map((l, i) => <p key={i}>{l}</p>)}
            </div>
          </div>
        )}
        <BigBtn testid="stockauto-save" onClick={submit} disabled={saving}>{saving ? "Subiendo..." : <><Plus size={18} /> Subir Stock Automático</>}</BigBtn>
      </Card>
    </div>
  );
}

/* Productos */
const emptyProd = { name: "", image: "", price: "", tags: [], category: "Streaming", terms: "", duration_days: 30, discount_pct: 0, delivery_mode: "stock", bulk_prices: [] };
const CATEGORY_SUGGESTIONS = ["Streaming", "Actas y Trámites", "Herramientas y Educación", "Gaming", "Otros"];
function Productos() {
  const { tenant } = useTenant();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ ...emptyProd });
  const [editId, setEditId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [tags, setTags] = useState([]);
  const load = () => api.get("/admin/products").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/admin/tags").then((r) => setTags(r.data)); }, []);
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) {
      return toast.error("Solo se aceptan imágenes PNG, JPG o WEBP");
    }
    if (f.size > MAX_LOGO_BYTES) {
      return toast.error("La imagen debe pesar menos de 10 MB");
    }
    const fd = new FormData(); fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, image: data.url }));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };
  const toggleTag = (name) => setForm((f) => ({ ...f, tags: f.tags.includes(name) ? f.tags.filter((t) => t !== name) : [...f.tags, name] }));
  const save = async () => {
    if (!form.name || form.price === "") return toast.error("Nombre y precio requeridos");
    const bulk_prices = (form.bulk_prices || [])
      .map((t) => ({ qty: Number(t.qty), price: Number(t.price) }))
      .filter((t) => t.qty > 0 && t.price >= 0);
    const body = { ...form, price: Number(form.price), duration_days: Number(form.duration_days) || 30, discount_pct: Number(form.discount_pct) || 0, active: true, stock_items: undefined, bulk_prices };
    delete body.stock_items;
    try {
      if (editId) { const cur = items.find((i) => i.id === editId); await api.put(`/admin/products/${editId}`, { ...body, stock_items: cur?.stock_items || [] }); }
      else await api.post("/admin/products", { ...body, stock_items: [] });
      toast.success("Servicio guardado"); setForm({ ...emptyProd }); setEditId(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const edit = (p) => { setEditId(p.id); setForm({ name: p.name, image: p.image, price: p.price, tags: p.tags || [], category: p.category, terms: p.terms, duration_days: p.duration_days, discount_pct: p.discount_pct || 0, delivery_mode: p.delivery_mode || "stock", bulk_prices: (p.bulk_prices || []).map((t) => ({ qty: t.qty, price: t.price })) }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const addBulkTier = () => setForm((f) => ({ ...f, bulk_prices: [...(f.bulk_prices || []), { qty: "", price: "" }] }));
  const setBulkTier = (idx, field, v) => setForm((f) => ({ ...f, bulk_prices: f.bulk_prices.map((t, i) => i === idx ? { ...t, [field]: v } : t) }));
  const removeBulkTier = (idx) => setForm((f) => ({ ...f, bulk_prices: f.bulk_prices.filter((_, i) => i !== idx) }));
  const confirmDelete = async () => {
    setDeleting(true);
    try { await api.delete(`/admin/products/${confirmDel.id}`); toast.success("Eliminado"); setConfirmDel(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };
  return (
    <div>
      <Title icon={StoreIcon}>Productos</Title>
      <Card className="p-6 mb-6">
        <CardHead icon={Plus}>{editId ? "Editar Servicio" : "Agregar Nuevo Servicio"}</CardHead>
        <Field label="Nombre del nuevo servicio:"><input data-testid="prod-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej: Netflix Premium" className={inputCls()} /></Field>
        <Field label="Categoría (para el filtro en la tienda):">
          <input data-testid="prod-category" list="prod-category-options" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ej: Streaming, Actas y Trámites, Herramientas..." className={inputCls()} />
          <datalist id="prod-category-options">
            {CATEGORY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
          </datalist>
        </Field>
        <Field label="Cómo se entrega:">
          <select data-testid="prod-delivery" value={form.delivery_mode} onChange={(e) => setForm({ ...form, delivery_mode: e.target.value })} className={inputCls()}>
            <option value="stock">Cuenta de Stock (correo:contraseña que ya subiste)</option>
            <option value="canva_invite">Invitación por correo (el cliente da su correo, tú lo agregas a mano en esa plataforma -- Canva, Duolingo, Gemini, etc.)</option>
          </select>
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Precio del servicio:"><input data-testid="prod-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="15.99" className={inputCls()} /></Field>
          <Field label="Descuento (%) — 0 = sin oferta:"><input data-testid="prod-discount" type="number" min="0" max="100" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} placeholder="0" className={inputCls()} /></Field>
        </div>
        {Number(form.discount_pct) > 0 && form.price !== "" && (
          <p className="text-xs text-emerald-600 font-semibold -mt-2 mb-4">
            Precio con oferta: {money(Number(form.price) * (1 - Number(form.discount_pct) / 100))} <span className="text-slate-400 font-normal line-through ml-1">{money(Number(form.price))}</span>
          </p>
        )}
        <Field label="Precios por paquete (opcional) — ej. 10 unidades por $130:">
          <div className="space-y-2">
            {(form.bulk_prices || []).map((t, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input type="number" min="1" value={t.qty} onChange={(e) => setBulkTier(idx, "qty", e.target.value)} placeholder="Cantidad (10)" className={inputCls()} />
                <span className="text-slate-400 text-sm">=</span>
                <input type="number" min="0" step="0.01" value={t.price} onChange={(e) => setBulkTier(idx, "price", e.target.value)} placeholder="Precio total (130)" className={inputCls()} />
                <button type="button" onClick={() => removeBulkTier(idx)} aria-label="Quitar paquete" className="text-slate-400 hover:text-red-600 shrink-0"><Trash2 size={16} /></button>
              </div>
            ))}
            <button type="button" onClick={addBulkTier} className="text-xs font-semibold text-primary hover:text-primary-hover inline-flex items-center gap-1"><Plus size={13} /> Agregar paquete</button>
            <p className="text-xs text-slate-400">Si el cliente lleva esa cantidad exacta (o un múltiplo), se le cobra el precio del paquete en vez del precio normal por unidad.</p>
          </div>
        </Field>
        <Field label="Etiquetas:">
          <div className="flex flex-wrap gap-2 border border-slate-200 rounded-xl p-4 bg-slate-50 min-h-[64px]">
            {tags.map((t) => {
              const on = form.tags.includes(t.name);
              return (
                <button key={t.id} type="button" onClick={() => toggleTag(t.name)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold border transition-colors ${on ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary hover:text-primary"}`}>
                  {on && <CheckCircle2 size={13} />} {t.name}
                </button>
              );
            })}
            {tags.length === 0 && <p className="text-slate-400 text-sm py-1">Sin etiquetas creadas todavía — créalas en "Etiquetas".</p>}
          </div>
        </Field>
        <Field label="Términos del servicio:"><textarea value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} rows={2} className={inputCls()} /></Field>
        <div className="grid sm:grid-cols-2 gap-4 items-end">
          <Field label="Imagen del servicio:">
            <input data-testid="prod-image" type="file" accept="image/*" onChange={onFile} disabled={uploading} className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-primary file:text-white file:font-semibold disabled:opacity-50" />
            {uploading && <p className="text-xs text-slate-400 mt-1">Subiendo imagen...</p>}
          </Field>
          {form.image && <img src={form.image} alt="preview" className="w-20 h-20 rounded-lg object-cover mb-4" />}
        </div>
        <BigBtn testid="prod-save" onClick={save} disabled={uploading}><Plus size={18} /> {editId ? "Guardar Cambios" : "Agregar Servicio"}</BigBtn>
        {editId && <button onClick={() => { setEditId(null); setForm({ ...emptyProd }); }} className="w-full mt-2 text-slate-500 text-sm font-semibold py-2">Cancelar edición</button>}
      </Card>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <Card key={p.id} className="p-4 flex gap-3">
            <img src={p.image || placeholderImage("MQ", tenant.primary_color)} alt="" loading="lazy" className="w-16 h-16 rounded-lg object-cover" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 truncate">{p.name}</p>
              {p.discount_pct > 0 ? (
                <p className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-primary font-bold">{money(p.price * (1 - p.discount_pct / 100))}</span>
                  <span className="text-slate-400 text-xs line-through">{money(p.price)}</span>
                  <span className="text-[10px] font-bold uppercase bg-primary/10 text-primary rounded px-1.5 py-0.5">-{p.discount_pct}%</span>
                </p>
              ) : (
                <p className="text-primary font-bold">{money(p.price)}</p>
              )}
              <p className="text-xs text-slate-400">{p.stock} uds en stock</p>
              {(p.bulk_prices || []).length > 0 && (
                <p className="text-[11px] text-emerald-600 font-semibold mt-0.5">
                  {p.bulk_prices.map((t) => `${t.qty}x = ${money(t.price)}`).join(" · ")}
                </p>
              )}
              <div className="flex gap-3 mt-2">
                <button data-testid={`edit-product-${p.id}`} onClick={() => edit(p)} className="text-xs font-semibold text-slate-600 hover:text-primary">Editar</button>
                <button onClick={() => setConfirmDel(p)} className="text-xs font-semibold text-red-600 hover:text-red-700 inline-flex items-center gap-1"><Trash2 size={12} /> Eliminar</button>
              </div>
            </div>
          </Card>
        ))}
      </div>
      {confirmDel && (
        <ConfirmDialog title="¿Eliminar este servicio?" message={`"${confirmDel.name}" se eliminará permanentemente, junto con su stock. Esta acción no se puede deshacer.`}
          busy={deleting} onConfirm={confirmDelete} onCancel={() => setConfirmDel(null)} />
      )}
    </div>
  );
}

/* Etiquetas */
function Etiquetas() {
  const [tags, setTags] = useState([]);
  const [name, setName] = useState("");
  const [products, setProducts] = useState([]);
  const [pid, setPid] = useState("");
  const [selected, setSelected] = useState([]);
  const load = () => api.get("/admin/tags").then((r) => setTags(r.data));
  const loadProducts = () => api.get("/admin/products").then((r) => { setProducts(r.data); setPid((cur) => cur || (r.data[0] && r.data[0].id) || ""); });
  useEffect(() => { load(); loadProducts(); }, []);
  useEffect(() => {
    const p = products.find((x) => x.id === pid);
    if (p) setSelected(p.tags || []);
  }, [pid, products]);

  const add = async () => { if (!name.trim()) return; await api.post("/admin/tags", { name: name.trim() }); setName(""); toast.success("Atributo agregado"); load(); };
  const delTag = async (t) => { await api.delete(`/admin/tags/${t.id}`); setSelected((s) => s.filter((x) => x !== t.name)); toast.success("Atributo eliminado"); load(); };
  const toggleAttr = (t) => setSelected((s) => s.includes(t) ? s.filter((x) => x !== t) : [...s, t]);
  const saveAttrs = async () => {
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    const body = { name: p.name, description: p.description, image: p.image, price: p.price, tags: selected,
      category: p.category, terms: p.terms, duration_days: p.duration_days, active: p.active, stock_items: p.stock_items || [] };
    await api.put(`/admin/products/${pid}`, body);
    toast.success("Atributos guardados"); loadProducts();
  };
  const selectedProduct = products.find((x) => x.id === pid);

  return (
    <div>
      <Title icon={Tags}>Etiquetas</Title>
      <Card className="p-6 mb-6">
        <CardHead icon={Plus}>Agregar Nuevo Atributo</CardHead>
        <label className="block">
          <span className="text-sm font-semibold text-slate-700 mb-1.5 block">Nuevo atributo:</span>
          <div className="flex gap-3">
            <input data-testid="tag-name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Ej: prime, 4k, hd..." className={`flex-1 ${inputCls()}`} />
            <button data-testid="tag-add" onClick={add} className="flex items-center gap-2 bg-primary hover:bg-primary-hover text-white font-bold rounded-lg px-5 transition-colors whitespace-nowrap"><Plus size={18} /> Agregar</button>
          </div>
        </label>
        {tags.length > 0 && (
          <div className="mt-5 pt-5 border-t border-slate-100">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">{tags.length} atributo{tags.length === 1 ? "" : "s"} creado{tags.length === 1 ? "" : "s"}</p>
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => (
                <span key={t.id} className="group inline-flex items-center gap-1.5 bg-primary/10 text-primary border border-primary/20 rounded-full pl-3 pr-1.5 py-1 text-sm font-semibold">
                  {t.name}
                  <button onClick={() => delTag(t)} aria-label={`Eliminar ${t.name}`} className="w-5 h-5 grid place-items-center rounded-full text-primary/60 hover:bg-primary hover:text-white transition-colors"><X size={12} /></button>
                </span>
              ))}
            </div>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <CardHead icon={Settings}>Asignar Atributos a Servicios</CardHead>
        <Field label="Servicio:">
          <select data-testid="attr-service" value={pid} onChange={(e) => setPid(e.target.value)} className={inputCls()}>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-slate-700">Atributos disponibles:</span>
          {selectedProduct && <span className="text-xs font-semibold text-slate-400">{selected.length} seleccionado{selected.length === 1 ? "" : "s"}</span>}
        </div>
        <div className="flex flex-wrap gap-2 border border-slate-200 rounded-xl p-4 mb-5 bg-slate-50 min-h-[64px]">
          {tags.map((t) => {
            const on = selected.includes(t.name);
            return (
              <button key={t.id} type="button" onClick={() => toggleAttr(t.name)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold border transition-colors ${on ? "bg-primary text-white border-primary shadow-sm" : "bg-white text-slate-600 border-slate-200 hover:border-primary hover:text-primary"}`}>
                {on && <CheckCircle2 size={13} />} {t.name}
              </button>
            );
          })}
          {tags.length === 0 && <p className="text-slate-400 text-sm py-1">Sin atributos creados todavía.</p>}
        </div>
        <BigBtn testid="attr-save" onClick={saveAttrs}><Save size={18} /> Guardar Atributos</BigBtn>
      </Card>
    </div>
  );
}

/* Créditos */
const creditsFmt = (n) => `${Number(n || 0).toFixed(2)} créditos`;
function Creditos() {
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState(null);
  const [amount, setAmount] = useState("");
  const [q, setQ] = useState("");
  const [q2, setQ2] = useState("");
  const [nu, setNu] = useState({ name: "", password: "", confirm: "", role: "client" });
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", credits: 0, role: "client", password: "" });
  const [confirmDelUser, setConfirmDelUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [accessBusy, setAccessBusy] = useState(null);
  const [confirmBlock, setConfirmBlock] = useState(null);
  const [confirmNewAdmin, setConfirmNewAdmin] = useState(false);
  const [confirmPromote, setConfirmPromote] = useState(false);
  const [promoteBusy, setPromoteBusy] = useState(false);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const setAccess = async (u, blocking) => {
    setAccessBusy(u.id);
    try {
      await api.post(`/admin/users/${u.id}/access`, { blocked: blocking });
      toast.success(blocking ? `${u.name} bloqueado` : `${u.name} reactivado`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAccessBusy(null); }
  };
  const toggleAccess = (u) => {
    const blocking = !(u.blocked_manual || u.blocked_auto);
    if (blocking) { setConfirmBlock(u); return; }
    setAccess(u, false);
  };
  const confirmBlockUser = async () => { await setAccess(confirmBlock, true); setConfirmBlock(null); };
  const addCredits = async () => {
    if (!sel || !amount) return toast.error("Selecciona usuario y monto");
    try { await api.post("/admin/credits", { user_id: sel.id, amount: Number(amount), note: "Recarga manual" }); toast.success("Créditos agregados"); setAmount(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const doCreateUser = async () => {
    try { await api.post("/admin/users", { name: nu.name, password: nu.password, role: nu.role }); toast.success("Cuenta creada"); setNu({ name: "", password: "", confirm: "", role: "client" }); load(); return true; }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); return false; }
  };
  const createUser = async () => {
    if (!nu.name || !nu.password) return toast.error("Completa los campos");
    if (nu.password !== nu.confirm) return toast.error("Las contraseñas no coinciden");
    if (nu.role === "admin") { setConfirmNewAdmin(true); return; }
    await doCreateUser();
  };
  const confirmDeleteUser = async () => {
    setDeletingUser(true);
    try { await api.delete(`/admin/users/${confirmDelUser.id}`); toast.success("Usuario eliminado"); setConfirmDelUser(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setDeletingUser(false); }
  };
  const openEdit = (u) => { setEditingUser(u); setEditForm({ name: u.name, credits: u.credits, role: u.role, password: "" }); };
  useEscapeClose(!!editingUser, () => setEditingUser(null));
  const doSaveEdit = async () => {
    try {
      await api.put(`/admin/users/${editingUser.id}`, { name: editForm.name, credits: Number(editForm.credits) || 0, role: editForm.role, password: editForm.password || null });
      toast.success("Usuario actualizado"); setEditingUser(null); load(); return true;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); return false; }
  };
  const saveEdit = async () => {
    if (!editForm.name.trim()) return toast.error("El nombre de usuario es requerido");
    if (editForm.role === "admin" && editingUser.role !== "admin") { setConfirmPromote(true); return; }
    await doSaveEdit();
  };
  const searchRes = q ? users.filter((u) => u.name.toLowerCase().includes(q.toLowerCase()) || u.username.toLowerCase().includes(q.toLowerCase())) : [];
  const listed = users.filter((u) => u.name.toLowerCase().includes(q2.toLowerCase()) || u.username.toLowerCase().includes(q2.toLowerCase()) || u.email.includes(q2));
  return (
    <div>
      <Title icon={UserPlus}>Créditos</Title>
      <Card className="p-6 mb-6">
        <CardHead icon={Wallet}>Agregar Créditos</CardHead>
        <Field label="Buscar usuario:">
          <input data-testid="credit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o usuario..." className={inputCls()} />
          {searchRes.length > 0 && <div className="mt-2 border border-slate-200 rounded-lg divide-y max-h-52 overflow-y-auto">{searchRes.map((u) => <button key={u.id} onClick={() => { setSel(u); setQ(""); }} className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex justify-between"><span className="font-semibold text-sm">{u.name} <span className="font-normal text-slate-400">@{u.username}</span></span><span className="text-xs text-primary">{money(u.credits)}</span></button>)}</div>}
        </Field>
        <Field label="Usuario seleccionado:"><div data-testid="credit-selected" className="w-full bg-slate-900 text-white rounded-lg px-4 py-3">{sel ? `${sel.name} (@${sel.username}) · ${sel.email} · ${money(sel.credits)}` : "Seleccione un usuario del buscador"}</div></Field>
        <Field label="Cantidad de créditos:"><input data-testid="credit-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="100.00" className={inputCls()} /></Field>
        <BigBtn testid="credit-submit" onClick={addCredits}><Wallet size={18} /> Agregar Créditos</BigBtn>
      </Card>

      <Card className="p-6 mb-6">
        <CardHead icon={UserPlus}>Crear Nueva Cuenta</CardHead>
        <Field label="Nombre de usuario:"><input data-testid="newuser-name" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} placeholder="Ingrese el nombre de usuario" className={inputCls()} /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Contraseña:"><input data-testid="newuser-pass" type="password" value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} placeholder="Ingrese la contraseña" className={inputCls()} /></Field>
          <Field label="Confirmar contraseña:"><input data-testid="newuser-confirm" type="password" value={nu.confirm} onChange={(e) => setNu({ ...nu, confirm: e.target.value })} placeholder="Confirme la contraseña" className={inputCls()} /></Field>
        </div>
        <Field label="Rol:"><select data-testid="newuser-role" value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value })} className={inputCls()}><option value="client">Cliente</option><option value="admin">Administrador</option></select></Field>
        <BigBtn testid="newuser-create" onClick={createUser}><UserPlus size={18} /> Crear Cuenta</BigBtn>
      </Card>

      <Card className="overflow-x-auto">
        <div className="p-5 pb-0"><CardHead icon={ListChecks}>Lista de Usuarios</CardHead></div>
        <div className="px-5 pb-4"><input value={q2} onChange={(e) => setQ2(e.target.value)} placeholder="Buscar usuario en la lista..." className={inputCls()} /></div>
        <table className="w-full text-sm min-w-[1000px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-5 py-3">ID</th>
              <th className="text-left px-5 py-3">Nombre de Usuario</th>
              <th className="text-right px-5 py-3">Créditos Disponibles</th>
              <th className="text-right px-5 py-3">Total Gastado</th>
              <th className="text-center px-5 py-3">Compras</th>
              <th className="text-center px-5 py-3">Rol</th>
              <th className="text-center px-5 py-3">Acceso</th>
              <th className="text-right px-5 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {listed.map((u, i) => {
              const blocked = u.blocked_manual || u.blocked_auto;
              return (
              <tr key={u.id} className="hover:bg-slate-50">
                <td className="px-5 py-3 text-slate-400">{i + 1}</td>
                <td className="px-5 py-3 font-semibold text-slate-900">{u.name}</td>
                <td className="px-5 py-3 text-right">
                  <p className="font-bold text-emerald-600">{creditsFmt(u.credits)}</p>
                  <span className="inline-block text-[10px] font-semibold text-emerald-700 bg-emerald-50 rounded px-2 py-0.5 mt-0.5">Disponibles</span>
                </td>
                <td className="px-5 py-3 text-right">
                  <p className="font-bold text-red-600">{creditsFmt(u.total_gastado)}</p>
                  <span className="inline-block text-[10px] font-semibold text-red-600 bg-red-50 rounded px-2 py-0.5 mt-0.5">Gastados</span>
                </td>
                <td className="px-5 py-3 text-center text-slate-600 whitespace-nowrap">{u.compras} compras</td>
                <td className="px-5 py-3 text-center whitespace-nowrap"><span className={`inline-block w-[104px] text-center text-xs font-bold px-2 py-1 rounded-full ${u.role === "admin" ? "bg-slate-900 text-white" : "bg-emerald-600 text-white"}`}>{u.role === "admin" ? "Administrador" : "Cliente"}</span></td>
                <td className="px-5 py-3 text-center whitespace-nowrap">
                  {u.role === "admin" ? (
                    <span className="text-slate-300 text-xs">—</span>
                  ) : (
                    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${blocked ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {blocked ? <XCircle size={12} /> : <CheckCircle2 size={12} />} {blocked ? (u.blocked_manual ? "Bloqueado" : "Bloqueado (inactivo)") : "Activo"}
                    </span>
                  )}
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} className="inline-flex items-center gap-1 bg-primary hover:bg-primary-hover text-white text-xs font-bold rounded-md px-3 py-2 transition-colors"><Pencil size={12} /> EDITAR</button>
                    {u.role !== "admin" && (
                      <button data-testid={`access-user-${u.id}`} onClick={() => toggleAccess(u)} disabled={accessBusy === u.id}
                        className={`inline-flex items-center gap-1 text-xs font-bold rounded-md px-3 py-2 transition-colors disabled:opacity-50 ${blocked ? "border border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border border-slate-300 text-slate-600 hover:border-red-400 hover:text-red-600"}`}>
                        {blocked ? <CheckCircle2 size={12} /> : <XCircle size={12} />} {accessBusy === u.id ? "..." : blocked ? "Reactivar" : "Bloquear"}
                      </button>
                    )}
                    {u.role !== "admin" && <button data-testid={`del-user-${u.id}`} onClick={() => setConfirmDelUser(u)} aria-label={`Eliminar ${u.name}`} className="w-8 h-8 grid place-items-center rounded-md text-red-600 hover:bg-red-50 transition-colors"><Trash2 size={16} /></button>}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {editingUser && (
        <div role="dialog" aria-modal="true" aria-label="Editar Usuario" className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditingUser(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg p-6 animate-fade-up shadow-md">
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-head text-xl font-extrabold text-slate-900 flex items-center gap-2"><Pencil size={18} className="text-primary" /> Editar Usuario</h3>
              <button onClick={() => setEditingUser(null)} aria-label="Cerrar" className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
            </div>
            <Field label="Nombre de usuario:"><input data-testid="edituser-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className={inputCls()} /></Field>
            <Field label="Créditos:"><input data-testid="edituser-credits" type="number" value={editForm.credits} onChange={(e) => setEditForm({ ...editForm, credits: e.target.value })} className={inputCls()} /></Field>
            <Field label="Rol:"><select data-testid="edituser-role" value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} className={inputCls()}><option value="client">Cliente</option><option value="admin">Administrador</option></select></Field>
            <Field label="Nueva contraseña (dejar en blanco para no cambiar):"><input data-testid="edituser-password" type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Nueva contraseña (opcional)" className={inputCls()} /></Field>
            <BigBtn testid="edituser-save" onClick={saveEdit}><Save size={18} /> Actualizar Usuario</BigBtn>
          </div>
        </div>
      )}
      {confirmDelUser && (
        <ConfirmDialog title="¿Eliminar este usuario?" message={`"${confirmDelUser.name}" y su historial se eliminarán permanentemente. Esta acción no se puede deshacer.`}
          busy={deletingUser} onConfirm={confirmDeleteUser} onCancel={() => setConfirmDelUser(null)} />
      )}
      {confirmBlock && (
        <ConfirmDialog title="¿Bloquear a este cliente?" confirmLabel="Bloquear" busyLabel="Bloqueando..."
          message={`"${confirmBlock.name}" perderá acceso a la tienda de inmediato. Puedes reactivarlo cuando quieras desde el mismo botón.`}
          busy={accessBusy === confirmBlock.id} onConfirm={confirmBlockUser} onCancel={() => setConfirmBlock(null)} />
      )}
      {confirmNewAdmin && (
        <ConfirmDialog title="¿Crear cuenta de Administrador?" confirmLabel="Crear administrador" busyLabel="Creando..."
          message={`"${nu.name}" tendrá acceso total al panel: créditos, ventas, usuarios (incluyendo poder crear otros administradores).`}
          busy={promoteBusy}
          onConfirm={async () => { setPromoteBusy(true); const ok = await doCreateUser(); setPromoteBusy(false); if (ok) setConfirmNewAdmin(false); }}
          onCancel={() => setConfirmNewAdmin(false)} />
      )}
      {confirmPromote && (
        <ConfirmDialog title="¿Ascender a Administrador?" confirmLabel="Ascender" busyLabel="Ascendiendo..."
          message={`"${editForm.name}" tendrá acceso total al panel: créditos, ventas, usuarios (incluyendo poder crear otros administradores).`}
          busy={promoteBusy}
          onConfirm={async () => { setPromoteBusy(true); const ok = await doSaveEdit(); setPromoteBusy(false); if (ok) setConfirmPromote(false); }}
          onCancel={() => setConfirmPromote(false)} />
      )}
    </div>
  );
}

/* Agregar Venta (entregada manual, fuera de la web) */
function VentaManual() {
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [productId, setProductId] = useState("");
  const [productName, setProductName] = useState("");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [deductStock, setDeductStock] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tipoEntrega, setTipoEntrega] = useState("completa"); // "completa" | "perfil"
  const [perfil, setPerfil] = useState("");
  const [fecha, setFecha] = useState(() => new Date().toLocaleDateString("en-CA"));
  const [saving, setSaving] = useState(false);
  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/products").then((r) => setProducts(r.data));
  };
  useEffect(() => { load(); }, []);
  const searchRes = q ? users.filter((u) => u.name.toLowerCase().includes(q.toLowerCase()) || u.username.toLowerCase().includes(q.toLowerCase())) : [];
  const pickProduct = (pid) => {
    setProductId(pid);
    if (!pid) { setProductName(""); setPrice(""); return; }
    const p = products.find((x) => x.id === pid);
    if (p) { setProductName(p.name); setPrice(p.discount_pct > 0 ? (p.price * (1 - p.discount_pct / 100)).toFixed(2) : p.price); }
  };
  const submit = async () => {
    if (!sel) return toast.error("Selecciona un cliente");
    if (!productName || price === "") return toast.error("Nombre del producto y precio requeridos");
    setSaving(true);
    try {
      await api.post("/admin/sales/manual", {
        user_id: sel.id, product_id: productId || null, product_name: productName,
        price: Number(price), quantity: Number(quantity) || 1, deduct_stock: deductStock,
        email: email || null, password: password || null,
        perfil: tipoEntrega === "perfil" ? (perfil || null) : null, fecha,
      });
      toast.success("Venta agregada");
      setSel(null); setProductId(""); setProductName(""); setPrice(""); setQuantity(1);
      setEmail(""); setPassword(""); setPerfil(""); setTipoEntrega("completa");
      setFecha(new Date().toLocaleDateString("en-CA"));
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const total = (Number(price) || 0) * (Number(quantity) || 0);
  return (
    <div>
      <Title icon={PackagePlus}>Agregar Venta</Title>
      <Card className="p-6 mb-6">
        <CardHead icon={PackagePlus}>Registrar venta entregada por fuera de la web</CardHead>
        <p className="text-slate-500 text-sm -mt-2 mb-4">Para cuando entregaste manual (prisa, WhatsApp, etc.) y quieres que cuente en el Leaderboard y en Resumen Créditos. No descuenta créditos del cliente — solo registra la venta.</p>
        <Field label="Buscar cliente:">
          <input data-testid="venta-manual-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o usuario..." className={inputCls()} />
          {searchRes.length > 0 && <div className="mt-2 border border-slate-200 rounded-lg divide-y max-h-52 overflow-y-auto">{searchRes.map((u) => <button key={u.id} onClick={() => { setSel(u); setQ(""); }} className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex justify-between"><span className="font-semibold text-sm">{u.name} <span className="font-normal text-slate-400">@{u.username}</span></span><span className="text-xs text-primary">{money(u.credits)}</span></button>)}</div>}
        </Field>
        <Field label="Cliente seleccionado:"><div data-testid="venta-manual-selected" className="w-full bg-slate-900 text-white rounded-lg px-4 py-3">{sel ? `${sel.name} (@${sel.username})` : "Seleccione un cliente del buscador"}</div></Field>
        <Field label="Producto del catálogo (opcional):">
          <select value={productId} onChange={(e) => pickProduct(e.target.value)} className={inputCls()}>
            <option value="">— Producto personalizado —</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Nombre a mostrar:"><input data-testid="venta-manual-name" value={productName} onChange={(e) => setProductName(e.target.value)} placeholder="Ej: Netflix Premium 1M" className={inputCls()} /></Field>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Precio unitario:"><input data-testid="venta-manual-price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" className={inputCls()} /></Field>
          <Field label="Cantidad:"><input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} className={inputCls()} /></Field>
          <Field label="Fecha de la compra:"><input data-testid="venta-manual-fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className={inputCls()} /></Field>
        </div>
        {productId && (
          <label className="flex items-center gap-2 text-sm text-slate-600 mb-4 -mt-2 cursor-pointer">
            <input type="checkbox" checked={deductStock} onChange={(e) => setDeductStock(e.target.checked)} />
            Descontar del stock de este producto
          </label>
        )}
        <Field label="Cuenta entregada (opcional — para que se vea en su historial de compras):">
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="venta-manual-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Correo" className={inputCls()} />
            <input data-testid="venta-manual-pass" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Contraseña" className={inputCls()} />
          </div>
          <div className="flex gap-2 mt-3">
            <button type="button" onClick={() => setTipoEntrega("completa")} className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold border transition-colors ${tipoEntrega === "completa" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary hover:text-primary"}`}>Cuenta completa</button>
            <button type="button" onClick={() => setTipoEntrega("perfil")} className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold border transition-colors ${tipoEntrega === "perfil" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary hover:text-primary"}`}>Con perfil</button>
          </div>
          {tipoEntrega === "perfil" && (
            <input data-testid="venta-manual-perfil" value={perfil} onChange={(e) => setPerfil(e.target.value)} placeholder="Número de perfil, ej: 2" className={`${inputCls()} mt-3`} />
          )}
        </Field>
        {price !== "" && <p className="text-sm text-slate-500 mb-4">Total: <b className="text-primary">{money(total)}</b></p>}
        <BigBtn testid="venta-manual-submit" onClick={submit} disabled={saving}><PackagePlus size={18} /> {saving ? "Guardando..." : "Agregar Venta"}</BigBtn>
      </Card>
    </div>
  );
}

/* Agregar Venta Masiva (varios clientes de un jalón) */
function VentaMasiva() {
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [fecha, setFecha] = useState(() => new Date().toLocaleDateString("en-CA"));
  const emptyRow = () => ({ key: Math.random().toString(36).slice(2), q: "", sel: null, productId: "", productName: "", price: "", quantity: 1, email: "", password: "", tipoEntrega: "completa", perfil: "" });
  const [rows, setRows] = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState(null);
  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/products").then((r) => setProducts(r.data));
  };
  useEffect(() => { load(); }, []);
  const updateRow = (key, patch) => setRows((rs) => rs.map((r) => r.key === key ? { ...r, ...patch } : r));
  const addRow = () => setRows((rs) => [...rs, emptyRow()]);
  const removeRow = (key) => setRows((rs) => rs.length > 1 ? rs.filter((r) => r.key !== key) : rs);
  const pickProduct = (key, pid) => {
    if (!pid) { updateRow(key, { productId: "", productName: "", price: "" }); return; }
    const p = products.find((x) => x.id === pid);
    if (p) updateRow(key, { productId: pid, productName: p.name, price: p.discount_pct > 0 ? (p.price * (1 - p.discount_pct / 100)).toFixed(2) : p.price });
  };
  const submit = async () => {
    const validRows = rows.filter((r) => r.sel && r.productName && r.price !== "");
    if (validRows.length === 0) return toast.error("Completa al menos una fila con cliente, producto y precio");
    setSaving(true);
    try {
      const { data } = await api.post("/admin/sales/manual/bulk", {
        fecha,
        items: validRows.map((r) => ({
          user_id: r.sel.id, product_id: r.productId || null, product_name: r.productName,
          price: Number(r.price), quantity: Number(r.quantity) || 1, deduct_stock: true,
          email: r.email || null, password: r.password || null,
          perfil: r.tipoEntrega === "perfil" ? (r.perfil || null) : null,
        })),
      });
      setResults(data.results);
      const ok = data.results.filter((x) => x.ok).length;
      if (ok === data.results.length) { toast.success(`${ok} ventas agregadas`); setRows([emptyRow()]); }
      else toast.error(`${ok}/${data.results.length} ventas agregadas — revisa los errores abajo`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <Title icon={ListChecks}>Agregar Venta Masiva</Title>
      <Card className="p-6 mb-6">
        <CardHead icon={ListChecks}>Registrar varias ventas entregadas por fuera de la web</CardHead>
        <p className="text-slate-500 text-sm -mt-2 mb-4">Para cargar de un jalón a varios clientes que le compraron por fuera. No descuenta créditos de nadie — solo registra las ventas para que cuenten en el Leaderboard y en Resumen Créditos.</p>
        <Field label="Fecha de las compras:"><input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className={`${inputCls()} max-w-xs`} /></Field>
        <div className="space-y-4 mb-2">
          {rows.map((r, i) => (
            <div key={r.key} className="border border-slate-200 rounded-xl p-4 relative">
              <p className="text-xs font-bold uppercase text-slate-400 mb-3">Fila {i + 1}</p>
              {rows.length > 1 && (
                <button onClick={() => removeRow(r.key)} aria-label="Quitar fila" className="absolute top-3 right-3 text-slate-400 hover:text-red-600"><X size={16} /></button>
              )}
              <Field label="Cliente:">
                {r.sel ? (
                  <div className="w-full bg-slate-900 text-white rounded-lg px-4 py-2.5 flex items-center justify-between">
                    <span className="text-sm">{r.sel.name} <span className="text-white/60">(@{r.sel.username})</span></span>
                    <button onClick={() => updateRow(r.key, { sel: null })} aria-label="Cambiar cliente" className="text-white/60 hover:text-white"><X size={14} /></button>
                  </div>
                ) : (
                  <>
                    <input value={r.q} onChange={(e) => updateRow(r.key, { q: e.target.value })} placeholder="Buscar por nombre o usuario..." className={inputCls()} />
                    {r.q && (
                      <div className="mt-2 border border-slate-200 rounded-lg divide-y max-h-40 overflow-y-auto">
                        {users.filter((u) => u.name.toLowerCase().includes(r.q.toLowerCase()) || u.username.toLowerCase().includes(r.q.toLowerCase())).map((u) => (
                          <button key={u.id} onClick={() => updateRow(r.key, { sel: u, q: "" })} className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm">{u.name} <span className="text-slate-400">@{u.username}</span></button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </Field>
              <div className="grid sm:grid-cols-4 gap-3">
                <div className="sm:col-span-2">
                  <Field label="Producto:">
                    <select value={r.productId} onChange={(e) => pickProduct(r.key, e.target.value)} className={inputCls()}>
                      <option value="">— Personalizado —</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Precio:"><input type="number" value={r.price} onChange={(e) => updateRow(r.key, { price: e.target.value })} placeholder="0.00" className={inputCls()} /></Field>
                <Field label="Cantidad:"><input type="number" min="1" value={r.quantity} onChange={(e) => updateRow(r.key, { quantity: e.target.value })} className={inputCls()} /></Field>
              </div>
              {!r.productId && (
                <Field label="Nombre a mostrar:"><input value={r.productName} onChange={(e) => updateRow(r.key, { productName: e.target.value })} placeholder="Ej: Netflix Premium 1M" className={inputCls()} /></Field>
              )}
              <Field label="Cuenta entregada (opcional — para su historial de compras):">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input value={r.email} onChange={(e) => updateRow(r.key, { email: e.target.value })} placeholder="Correo" className={inputCls()} />
                  <input value={r.password} onChange={(e) => updateRow(r.key, { password: e.target.value })} placeholder="Contraseña" className={inputCls()} />
                </div>
                <div className="flex gap-2 mt-3">
                  <button type="button" onClick={() => updateRow(r.key, { tipoEntrega: "completa" })} className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold border transition-colors ${r.tipoEntrega === "completa" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary hover:text-primary"}`}>Cuenta completa</button>
                  <button type="button" onClick={() => updateRow(r.key, { tipoEntrega: "perfil" })} className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold border transition-colors ${r.tipoEntrega === "perfil" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary hover:text-primary"}`}>Con perfil</button>
                </div>
                {r.tipoEntrega === "perfil" && (
                  <input value={r.perfil} onChange={(e) => updateRow(r.key, { perfil: e.target.value })} placeholder="Número de perfil, ej: 2" className={`${inputCls()} mt-3`} />
                )}
              </Field>
            </div>
          ))}
        </div>
        <button onClick={addRow} className="mb-5 flex items-center gap-1.5 text-primary hover:text-primary-hover font-semibold text-sm"><Plus size={16} /> Agregar otra fila</button>
        <BigBtn testid="venta-masiva-submit" onClick={submit} disabled={saving}><ListChecks size={18} /> {saving ? "Guardando..." : `Guardar ${rows.length > 1 ? `${rows.length} ventas` : "venta"}`}</BigBtn>
      </Card>
      {results && (
        <Card className="p-6">
          <CardHead icon={CheckCircle2}>Resultado</CardHead>
          <div className="space-y-2">
            {results.map((r, i) => (
              <div key={i} className={`flex items-center justify-between gap-3 rounded-lg px-4 py-2.5 ${r.ok ? "bg-emerald-50" : "bg-red-50"}`}>
                <span className="text-sm">{r.ok ? `${r.user_name} — ${r.product_name}` : `${r.product_name}: ${r.error}`}</span>
                {r.ok && <span className="font-bold text-emerald-700 text-sm">{money(r.total)}</span>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

/* Resumen */
function StatTile({ value, label, color }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4 text-center">
      <p className={`font-head text-xl font-extrabold ${color}`}>{value}</p>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mt-1">{label}</p>
    </div>
  );
}
function Resumen() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("todos");
  const [detail, setDetail] = useState(null);
  const [detailTxs, setDetailTxs] = useState(null);
  const load = () => api.get("/admin/credits/summary").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  useEscapeClose(!!detail, () => setDetail(null));
  const openDetail = (u) => {
    setDetail(u);
    setDetailTxs(null);
    // Historial completo de ESE usuario, no filtrado de una lista global
    // capada -- un usuario con actividad antigua se quedaba sin ver sus
    // propios movimientos si no estaban entre los más recientes del tenant.
    api.get(`/admin/credits/user/${u.id}/transactions`).then((r) => setDetailTxs(r.data)).catch(() => setDetailTxs([]));
  };
  if (!d) return <Loading />;

  const mostrarTodos = () => { setQ(""); setEstado("todos"); };
  const filtered = d.users.filter((u) => {
    const okQ = !q || u.name.toLowerCase().includes(q.toLowerCase()) || u.username.toLowerCase().includes(q.toLowerCase());
    const okE = estado === "todos" || (estado === "activos" ? u.activo : !u.activo);
    return okQ && okE;
  });
  const userTxs = detailTxs || [];

  return (
    <div>
      <Title icon={Wallet}>Resumen Créditos</Title>

      <Card className="p-6 mb-6">
        <CardHead icon={PieChart}>Resumen General de Créditos</CardHead>
        <div className="border-l-4 border-red-500 bg-rose-50/40 rounded-r-lg pl-4 pr-2 py-4">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">Estadísticas Globales de Créditos</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatTile value={money(d.global.disponibles)} label="Créditos Disponibles" color="text-emerald-600" />
            <StatTile value={money(d.global.gastados)} label="Créditos Gastados" color="text-red-600" />
            <StatTile value={money(d.global.total)} label="Total Créditos" color="text-slate-900" />
            <StatTile value={d.global.compras_totales} label="Compras Totales" color="text-blue-600" />
            <StatTile value={d.global.usuarios_activos} label="Usuarios Activos" color="text-amber-500" />
            <StatTile value={money(d.global.promedio_compra)} label="Promedio por Compra" color="text-slate-900" />
          </div>
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <div className="p-5 pb-0"><CardHead icon={Users}>Detalle por Usuario</CardHead></div>
        <div className="px-5 pb-5 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar usuario por nombre..." className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
          </div>
          <select value={estado} onChange={(e) => setEstado(e.target.value)} className="border border-slate-200 rounded-lg px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-red-500">
            <option value="todos">Todos los usuarios</option>
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
          </select>
          <button onClick={mostrarTodos} className="flex items-center justify-center gap-1.5 bg-slate-700 hover:bg-slate-800 text-white font-bold rounded-lg px-5 py-3 transition-colors"><RotateCcw size={15} /> Mostrar todos</button>
        </div>
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-5 py-3">Usuario</th>
              <th className="text-right px-5 py-3">Créditos Disponibles</th>
              <th className="text-right px-5 py-3">Total Gastado</th>
              <th className="text-right px-5 py-3">Total Invertido</th>
              <th className="text-center px-5 py-3">Compras Realizadas</th>
              <th className="text-right px-5 py-3">Promedio por Compra</th>
              <th className="text-center px-5 py-3">Estado</th>
              <th className="text-right px-5 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((u) => (
              <tr key={u.id} className="hover:bg-slate-50">
                <td className="px-5 py-3">
                  <p className="font-semibold text-slate-900">{u.username}</p>
                  <p className="text-xs text-slate-400">ID: {u.id.slice(-4)}</p>
                </td>
                <td className="px-5 py-3 text-right">
                  <p className="font-bold text-emerald-600">{money(u.disponibles)}</p>
                  <span className="inline-block text-[10px] font-semibold text-emerald-700 bg-emerald-50 rounded px-2 py-0.5 mt-0.5">Disponibles</span>
                </td>
                <td className="px-5 py-3 text-right">
                  <p className="font-bold text-red-600">{money(u.gastado)}</p>
                  <span className="inline-block text-[10px] font-semibold text-red-600 bg-red-50 rounded px-2 py-0.5 mt-0.5">Gastados</span>
                </td>
                <td className="px-5 py-3 text-right font-bold text-blue-600">{money(u.invertido)}</td>
                <td className="px-5 py-3 text-center text-slate-600">{u.compras}</td>
                <td className="px-5 py-3 text-right font-semibold text-slate-700">{money(u.promedio)}</td>
                <td className="px-5 py-3 text-center">
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${u.activo ? "bg-emerald-600 text-white" : "bg-red-900 text-white"}`}>{u.activo ? "Activo" : "Inactivo"}</span>
                </td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => openDetail(u)} className="inline-flex items-center gap-1 border border-slate-300 hover:border-primary hover:text-primary text-slate-600 text-xs font-bold rounded-md px-3 py-2 transition-colors"><Eye size={13} /> VER DETALLE</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-slate-400 p-8 text-center">Sin resultados.</p>}
      </Card>

      {detail && (
        <div role="dialog" aria-modal="true" aria-label={detail.username} className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden animate-fade-up shadow-md">
            <div className="flex items-center justify-between px-6 py-4 bg-primary text-white shrink-0">
              <h3 className="font-head text-lg font-extrabold">{detail.username}</h3>
              <button onClick={() => setDetail(null)} aria-label="Cerrar" className="text-white/80 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="grid grid-cols-3 gap-3 mb-5">
                <StatTile value={money(detail.disponibles)} label="Disponibles" color="text-emerald-600" />
                <StatTile value={money(detail.gastado)} label="Gastado" color="text-red-600" />
                <StatTile value={detail.compras} label="Compras" color="text-slate-900" />
              </div>
              {detailTxs === null ? (
                <p className="text-slate-400 text-center py-8">Cargando movimientos...</p>
              ) : userTxs.length === 0 ? (
                <p className="text-slate-400 text-center py-8">Sin movimientos registrados.</p>
              ) : (
                <div className="space-y-2">
                  {userTxs.map((t) => (
                    <div key={t.id} className="flex items-center justify-between gap-3 bg-slate-50 rounded-lg px-4 py-2.5">
                      <div className="min-w-0">
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${t.amount >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{t.type}</span>
                        <p className="text-sm text-slate-600 mt-1 truncate" title={t.note}>{t.note}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className={`font-bold text-sm ${t.amount >= 0 ? "text-emerald-600" : "text-red-600"}`}>{t.amount >= 0 ? "+" : ""}{money(t.amount)}</p>
                        <p className="text-[11px] text-slate-400">{fmtDate(t.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Recargas Automáticas */
function RecargasAutomaticas() {
  const [reqs, setReqs] = useState(null);
  const [approving, setApproving] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [confirmApprove, setConfirmApprove] = useState(null);
  const load = () => api.get("/admin/recharge-requests").then((r) => setReqs(r.data));
  useEffect(() => { load(); }, []);
  if (!reqs) return <Loading />;
  const pendientes = reqs.filter((r) => r.status === "pendiente");
  const acreditadas = reqs.filter((r) => r.status === "acreditado");
  const montoAcreditado = acreditadas.reduce((s, r) => s + (r.monto || 0), 0);
  const stats = [
    { icon: Clock, label: "Pendientes", value: pendientes.length, color: "text-amber-500" },
    { icon: CheckCircle2, label: "Acreditadas", value: acreditadas.length, color: "text-emerald-600" },
    { icon: Wallet, label: "Total acreditado", value: money(montoAcreditado), color: "text-primary" },
  ];
  const confirmApproveReq = async () => {
    const r = confirmApprove;
    setApproving(r.id);
    try { await api.post(`/admin/recharge-requests/${r.id}/approve`); toast.success(`Recarga de ${r.usuario_reportado} acreditada`); setConfirmApprove(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setApproving(null); }
  };
  const confirmCancelReq = async () => {
    setCancelling(confirmCancel.id);
    try { await api.post(`/admin/recharge-requests/${confirmCancel.id}/cancel`); toast.success("Solicitud cancelada"); setConfirmCancel(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCancelling(null); }
  };
  const estadoCls = (s) => s === "acreditado" ? "bg-emerald-600 text-white" : s === "cancelado" ? "bg-slate-300 text-slate-700" : "bg-amber-100 text-amber-700";
  const estadoLabel = (s) => s === "acreditado" ? "Acreditado" : s === "cancelado" ? "Cancelado" : "Pendiente";
  return (
    <div>
      <Title icon={Bot}>Recargas Automáticas</Title>
      <p className="text-sm text-slate-500 -mt-4 mb-6">Solicitudes recibidas por tu bot de Telegram. Bitso (transferencia) se cruza contra las confirmaciones bancarias de tu correo; Binance Pay se cruza contra tu historial real de Binance por Order ID exacto. Ambos se acreditan solos — el botón "Aprobar" queda como respaldo manual por si algo no coincide.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {stats.map((s) => (
          <Card key={s.label} className="p-5">
            <div className="flex items-center gap-2 text-slate-500 mb-1"><s.icon size={14} className="text-primary" /><p className="text-[11px] font-semibold uppercase tracking-wide">{s.label}</p></div>
            <p className={`font-head text-2xl font-extrabold ${s.color}`}>{s.value}</p>
          </Card>
        ))}
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-5 py-3">Usuario reportado</th>
              <th className="text-left px-5 py-3">Nombre</th>
              <th className="text-left px-5 py-3">Método</th>
              <th className="text-left px-5 py-3">Detalle de pago</th>
              <th className="text-right px-5 py-3">Monto</th>
              <th className="text-center px-5 py-3">Estado</th>
              <th className="text-right px-5 py-3">Fecha</th>
              <th className="text-right px-5 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {reqs.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50">
                <td className="px-5 py-3 font-semibold text-slate-900">{r.usuario_reportado}{!r.user_id && <span className="ml-2 text-[10px] font-bold uppercase text-red-600">sin cuenta</span>}</td>
                <td className="px-5 py-3 text-slate-600">{r.nombre_reportado}</td>
                <td className="px-5 py-3 whitespace-nowrap">
                  {r.metodo_pago === "binance" ? (
                    <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800">🟡 Binance</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-primary/10 text-primary">🏦 Bitso</span>
                  )}
                </td>
                <td className="px-5 py-3 text-slate-600">
                  {r.metodo_pago === "binance" ? (
                    r.binance_order_id ? <span className="font-mono text-xs">{r.binance_order_id}</span> : <span className="text-slate-300">—</span>
                  ) : r.remitente_bancario ? (
                    <span className="inline-flex items-center gap-1.5">
                      {r.nombre_coincide ? <CheckCircle2 size={14} className="text-emerald-600 shrink-0" /> : <AlertTriangle size={14} className="text-amber-500 shrink-0" />}
                      {r.remitente_bancario}
                    </span>
                  ) : r.ultimo_intento_remitente ? (
                    <span className="inline-flex items-center gap-1.5 text-amber-600" title={`No coincidió con "${r.nombre_reportado}" — último intento ${fmtDate(r.ultimo_intento_at)}`}>
                      <AlertTriangle size={14} className="shrink-0" /> {r.ultimo_intento_remitente} <span className="text-[10px] text-slate-400">(no coincide)</span>
                    </span>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-5 py-3 text-right font-bold text-slate-900">
                  {r.metodo_pago === "binance" && r.status === "pendiente" ? (
                    <span className="text-slate-500 font-semibold">≈ {r.monto} {r.monto_binance_moneda || "USDT"} <span className="text-[10px] text-slate-400">(por confirmar)</span></span>
                  ) : r.metodo_pago === "binance" && r.monto_binance_crypto ? (
                    <span title={`${r.monto_binance_crypto} ${r.monto_binance_moneda} x ${r.tipo_cambio_aplicado} MXN`}>{money(r.monto)}</span>
                  ) : money(r.monto)}
                </td>
                <td className="px-5 py-3 text-center"><span className={`text-xs font-bold px-3 py-1 rounded-full ${estadoCls(r.status)}`}>{estadoLabel(r.status)}</span></td>
                <td className="px-5 py-3 text-right text-slate-400 text-xs whitespace-nowrap">{fmtDate(r.created_at)}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  {r.status === "pendiente" && (
                    <div className="flex items-center justify-end gap-2">
                      {r.user_id && (
                        <button onClick={() => setConfirmApprove(r)} disabled={approving === r.id || cancelling === r.id} className="inline-flex items-center gap-1 border border-slate-300 hover:border-primary hover:text-primary disabled:opacity-50 text-slate-600 text-xs font-bold rounded-md px-3 py-1.5 transition-colors">
                          <CheckCircle2 size={13} /> {approving === r.id ? "Aprobando..." : "Aprobar"}
                        </button>
                      )}
                      <button onClick={() => setConfirmCancel(r)} disabled={approving === r.id || cancelling === r.id} className="inline-flex items-center gap-1 border border-slate-300 hover:border-red-400 hover:text-red-600 disabled:opacity-50 text-slate-600 text-xs font-bold rounded-md px-3 py-1.5 transition-colors">
                        <XCircle size={13} /> Cancelar
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {reqs.length === 0 && (
          <div className="p-10 text-center">
            <Bot size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-400">Sin solicitudes de recarga todavía.</p>
            <p className="text-slate-300 text-sm mt-1">Aquí verás las que lleguen por tu bot de Telegram.</p>
          </div>
        )}
      </Card>
      {confirmCancel && (
        <ConfirmDialog title="¿Cancelar esta solicitud?" confirmLabel="Cancelar solicitud"
          message={`La solicitud de ${confirmCancel.usuario_reportado} por ${money(confirmCancel.monto)} quedará marcada como cancelada y ya no se podrá acreditar automáticamente.`}
          busy={cancelling === confirmCancel?.id} onConfirm={confirmCancelReq} onCancel={() => setConfirmCancel(null)} />
      )}
      {confirmApprove && (
        <ConfirmDialog title="¿Aprobar esta recarga?" confirmLabel="Aprobar" busyLabel="Aprobando..."
          message={`Se acreditarán ${money(confirmApprove.monto)} a ${confirmApprove.usuario_reportado} de inmediato. Verifica que sea la fila correcta antes de continuar.`}
          busy={approving === confirmApprove.id} onConfirm={confirmApproveReq} onCancel={() => setConfirmApprove(null)} />
      )}
    </div>
  );
}

/* Cupones */
const emptyCoupon = { code: "", monto: "", usos_max: "", expira_at: "" };
function Cupones() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({ ...emptyCoupon });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const load = () => api.get("/admin/coupons").then((r) => setCoupons(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code.trim()) return toast.error("Escribe un código");
    if (!form.monto || Number(form.monto) <= 0) return toast.error("El monto debe ser mayor a 0");
    setSaving(true);
    try {
      await api.post("/admin/coupons", {
        code: form.code.trim(), monto: Number(form.monto),
        usos_max: form.usos_max ? Number(form.usos_max) : null,
        expira_at: form.expira_at ? new Date(form.expira_at).toISOString() : null,
      });
      toast.success("Cupón creado"); setForm({ ...emptyCoupon }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const toggle = async (c) => {
    setBusyId(c.id);
    try { await api.post(`/admin/coupons/${c.id}/toggle`); toast.success(c.activo ? "Cupón desactivado" : "Cupón activado"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };
  const confirmDelete = async () => {
    setBusyId(confirmDel.id);
    try { await api.delete(`/admin/coupons/${confirmDel.id}`); toast.success("Cupón eliminado"); setConfirmDel(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };
  const estadoCls = (e) => e === "activo" ? "bg-emerald-100 text-emerald-700" : e === "agotado" ? "bg-amber-100 text-amber-700" : e === "expirado" ? "bg-slate-200 text-slate-600" : "bg-red-100 text-red-700";
  const estadoLabel = (e) => ({ activo: "Activo", agotado: "Agotado", expirado: "Expirado", desactivado: "Desactivado" }[e] || e);

  return (
    <div>
      <Title icon={Ticket}>Cupones</Title>
      <Card className="p-6 mb-6">
        <CardHead icon={Plus}>Crear Cupón de Fidelidad</CardHead>
        <p className="text-xs text-slate-500 mb-4">Genera un código que tus clientes puedan canjear en la tienda a cambio de créditos — para recompensar clientes frecuentes, campañas de bienvenida, etc.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Código:"><input data-testid="coupon-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="FIDELIDAD50" className={inputCls()} /></Field>
          <Field label="Créditos que otorga:"><input data-testid="coupon-monto" type="number" value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} placeholder="50" className={inputCls()} /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Usos máximos (en blanco = ilimitado):"><input data-testid="coupon-usos" type="number" min="1" value={form.usos_max} onChange={(e) => setForm({ ...form, usos_max: e.target.value })} placeholder="Ilimitado" className={inputCls()} /></Field>
          <Field label="Expira (opcional):"><input data-testid="coupon-expira" type="date" value={form.expira_at} onChange={(e) => setForm({ ...form, expira_at: e.target.value })} className={inputCls()} /></Field>
        </div>
        <BigBtn testid="coupon-save" onClick={create}>{saving ? "Creando..." : <><Ticket size={18} /> Crear Cupón</>}</BigBtn>
      </Card>

      <Card className="overflow-x-auto">
        <div className="p-5 pb-0"><CardHead icon={ListChecks}>Cupones Creados</CardHead></div>
        <table className="w-full text-sm min-w-[680px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-5 py-3">Código</th>
              <th className="text-right px-5 py-3">Créditos</th>
              <th className="text-center px-5 py-3">Usos</th>
              <th className="text-center px-5 py-3">Expira</th>
              <th className="text-center px-5 py-3">Estado</th>
              <th className="text-right px-5 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {coupons.map((c) => (
              <tr key={c.id} className="hover:bg-slate-50">
                <td className="px-5 py-3 font-mono font-bold text-slate-900">{c.code}</td>
                <td className="px-5 py-3 text-right font-bold text-primary">{money(c.monto)}</td>
                <td className="px-5 py-3 text-center text-slate-600">{c.usos_actuales}{c.usos_max ? ` / ${c.usos_max}` : " / ∞"}</td>
                <td className="px-5 py-3 text-center text-slate-500 text-xs">{c.expira_at ? new Date(c.expira_at).toLocaleDateString("es-MX") : "—"}</td>
                <td className="px-5 py-3 text-center"><span className={`text-xs font-bold px-2.5 py-1 rounded-full ${estadoCls(c.estado)}`}>{estadoLabel(c.estado)}</span></td>
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button data-testid={`coupon-toggle-${c.id}`} onClick={() => toggle(c)} disabled={busyId === c.id}
                      className="inline-flex items-center gap-1 border border-slate-300 hover:border-primary hover:text-primary text-slate-600 text-xs font-bold rounded-md px-3 py-2 transition-colors disabled:opacity-50">
                      {c.activo ? "Desactivar" : "Activar"}
                    </button>
                    <button data-testid={`coupon-del-${c.id}`} onClick={() => setConfirmDel(c)} aria-label={`Eliminar ${c.code}`} className="w-8 h-8 grid place-items-center rounded-md text-red-600 hover:bg-red-50 transition-colors"><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {coupons.length === 0 && (
          <div className="p-10 text-center">
            <Ticket size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-400">Sin cupones creados todavía.</p>
          </div>
        )}
      </Card>
      {confirmDel && (
        <ConfirmDialog title="¿Eliminar este cupón?" message={`"${confirmDel.code}" dejará de poder canjearse. Esta acción no se puede deshacer.`}
          busy={busyId === confirmDel?.id} onConfirm={confirmDelete} onCancel={() => setConfirmDel(null)} />
      )}
    </div>
  );
}

/* Ruleta de premios */
const emptyPrizeRow = { label: "", tipo: "saldo", valor: "", product_id: "", weight: "" };

function Ruleta() {
  const [campaigns, setCampaigns] = useState([]);
  const [products, setProducts] = useState([]);
  const [prizes, setPrizes] = useState([{ ...emptyPrizeRow }]);
  const [cupoTotal, setCupoTotal] = useState(20);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [spinsFor, setSpinsFor] = useState(null); // campaña cuyo detalle de ganadores se está viendo
  const [spins, setSpins] = useState([]);
  const [loadingSpins, setLoadingSpins] = useState(false);

  const load = () => api.get("/admin/roulette").then((r) => setCampaigns(r.data));
  useEffect(() => { load(); api.get("/admin/products").then((r) => setProducts(r.data)); }, []);

  const totalWeight = prizes.reduce((s, p) => s + (Number(p.weight) || 0), 0);

  const addPrize = () => setPrizes((p) => [...p, { ...emptyPrizeRow }]);
  const removePrize = (i) => setPrizes((p) => p.filter((_, idx) => idx !== i));
  const updPrize = (i, k, v) => setPrizes((p) => p.map((row, idx) => {
    if (idx !== i) return row;
    if (k === "tipo") return { ...row, tipo: v, product_id: "", valor: v === "saldo" ? "" : row.valor };
    // Al elegir el producto, se autocompleta el valor con su precio real del
    // catálogo (ya vive en Productos) y el nombre con una versión presentable
    // -- el catálogo tiene nombres técnicos ("CRUNCHY ROLL MF PERFIL"), y en
    // la ruleta se ve mejor "Crunchyroll · Perfil". El admin puede editarlo
    // después si quiere otro texto.
    if (k === "product_id") {
      const prod = products.find((p) => p.id === v);
      return { ...row, product_id: v, valor: prod ? String(prod.price) : row.valor, label: prod ? prettyProductName(prod.name) : row.label };
    }
    return { ...row, [k]: v };
  }));

  // Repite la configuración de la última ruleta que se creó -- para no
  // tener que volver a escribir los mismos 9-13 premios cada vez que se
  // quiere lanzar una tanda nueva con la misma tabla de premios.
  const duplicarUltima = () => {
    if (!campaigns[0]) return;
    setPrizes(campaigns[0].prizes.map((p) => ({ label: p.label, tipo: p.tipo, valor: String(p.valor), product_id: p.product_id || "", weight: String(p.weight) })));
    setCupoTotal(campaigns[0].cupo_total);
    toast.success("Configuración copiada de la última ruleta");
  };

  const crear = async () => {
    if (prizes.length === 0) return toast.error("Agrega al menos un premio");
    if (prizes.some((p) => !p.label.trim() || p.valor === "" || p.weight === "")) return toast.error("Completa nombre, valor y probabilidad de cada premio");
    if (prizes.some((p) => p.tipo === "producto" && !p.product_id)) return toast.error("Selecciona un producto para cada premio de tipo \"Producto\"");
    if (!cupoTotal || Number(cupoTotal) < 1) return toast.error("El cupo de premios debe ser al menos 1");
    setCreating(true);
    try {
      await api.post("/admin/roulette", {
        prizes: prizes.map((p) => ({
          label: p.label.trim(), tipo: p.tipo, valor: Number(p.valor),
          product_id: p.tipo === "producto" ? p.product_id : null, weight: Number(p.weight),
        })),
        cupo_total: Number(cupoTotal),
      });
      toast.success("Ruleta creada en pausa -- actívala cuando quieras regalar premios");
      setPrizes([{ ...emptyPrizeRow }]); setCupoTotal(20);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const activar = async (id) => {
    setBusyId(id);
    try { await api.post(`/admin/roulette/${id}/activate`); toast.success("¡Ruleta activada! Ya la ven tus clientes."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };
  const desactivar = async (id) => {
    setBusyId(id);
    try { await api.post(`/admin/roulette/${id}/deactivate`); toast.success("Ruleta desactivada"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };
  const confirmDelete = async () => {
    setBusyId(confirmDel.id);
    try { await api.delete(`/admin/roulette/${confirmDel.id}`); toast.success("Ruleta eliminada"); setConfirmDel(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };
  const verGanadores = async (c) => {
    setSpinsFor(c.id); setLoadingSpins(true);
    try { const { data } = await api.get(`/admin/roulette/${c.id}/spins`); setSpins(data); }
    finally { setLoadingSpins(false); }
  };

  const estado = (c) => c.active ? "activa" : c.cupo_restante <= 0 ? "agotada" : "pausada";
  const estadoCls = (e) => e === "activa" ? "bg-emerald-100 text-emerald-700" : e === "agotada" ? "bg-slate-200 text-slate-600" : "bg-amber-100 text-amber-700";
  const estadoLabel = (e) => ({ activa: "Activa", agotada: "Agotada", pausada: "Pausada" }[e]);

  return (
    <div>
      <Title icon={Gift}>Ruleta de Premios</Title>
      <Card className="p-6 mb-6">
        <CardHead icon={Plus}>Nueva Ruleta</CardHead>
        <p className="text-xs text-slate-500 mb-4">
          Define los premios y cuántos vas a regalar en esta tanda. Se crea en pausa -- tus clientes no la ven hasta
          que la actives. En cuanto se acabe el cupo, se apaga sola. Cada cliente puede tirar una sola vez por ruleta.
        </p>

        {campaigns[0] && (
          <button type="button" onClick={duplicarUltima} className="mb-4 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-primary border border-slate-200 hover:border-primary rounded-md px-3 py-1.5 transition-colors">
            <Copy size={13} /> Repetir la configuración de la última ruleta
          </button>
        )}

        <div className="space-y-3 mb-4">
          {prizes.map((p, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1.3fr_0.9fr_1.3fr_0.8fr_0.8fr_auto] gap-2 items-center bg-slate-50 border border-slate-200 rounded-xl p-3">
              <input data-testid={`prize-label-${i}`} value={p.label} onChange={(e) => updPrize(i, "label", e.target.value)} placeholder="Nombre del premio" className={inputCls()} />
              <select data-testid={`prize-tipo-${i}`} value={p.tipo} onChange={(e) => updPrize(i, "tipo", e.target.value)} className={inputCls()}>
                <option value="saldo">Saldo</option>
                <option value="producto">Producto</option>
              </select>
              {p.tipo === "producto" ? (
                <select data-testid={`prize-product-${i}`} value={p.product_id} onChange={(e) => updPrize(i, "product_id", e.target.value)} className={inputCls()}>
                  <option value="">Selecciona el producto...</option>
                  {products.map((prod) => <option key={prod.id} value={prod.id}>{prod.name}</option>)}
                </select>
              ) : (
                <div />
              )}
              <input data-testid={`prize-valor-${i}`} type="number" min="0" value={p.valor} readOnly={p.tipo === "producto"}
                onChange={(e) => updPrize(i, "valor", e.target.value)} placeholder="Valor $"
                className={`${inputCls()} ${p.tipo === "producto" ? "bg-slate-100 text-slate-500" : ""}`} />
              <input data-testid={`prize-weight-${i}`} type="number" min="0" step="0.1" value={p.weight} onChange={(e) => updPrize(i, "weight", e.target.value)} placeholder="Prob. %" className={inputCls()} />
              <button type="button" onClick={() => removePrize(i)} aria-label="Quitar premio" className="w-11 h-11 grid place-items-center rounded-lg text-red-600 hover:bg-red-50 transition-colors"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <button type="button" onClick={addPrize} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary-hover"><Plus size={16} /> Agregar premio</button>

        <p className={`text-xs font-semibold mb-4 ${Math.round(totalWeight) === 100 ? "text-emerald-600" : "text-amber-600"}`}>
          Suma de probabilidades: {totalWeight}% {Math.round(totalWeight) !== 100 && "(no tiene que sumar exacto 100, pero ayuda para que sea intuitivo)"}
        </p>

        <Field label="¿Cuántos premios vas a regalar en esta tanda?">
          <input data-testid="ruleta-cupo" type="number" min="1" value={cupoTotal} onChange={(e) => setCupoTotal(e.target.value)} className={inputCls()} />
        </Field>

        <BigBtn testid="ruleta-save" onClick={crear} disabled={creating}>{creating ? "Creando..." : <><Gift size={18} /> Crear Ruleta (en pausa)</>}</BigBtn>
      </Card>

      <Card className="overflow-x-auto">
        <div className="p-5 pb-0"><CardHead icon={ListChecks}>Ruletas Creadas</CardHead></div>
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-5 py-3">Creada</th>
              <th className="text-center px-5 py-3">Premios</th>
              <th className="text-center px-5 py-3">Cupo</th>
              <th className="text-center px-5 py-3">Estado</th>
              <th className="text-right px-5 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {campaigns.map((c) => {
              const e = estado(c);
              return (
                <tr key={c.id} className="hover:bg-slate-50 align-top">
                  <td className="px-5 py-3 text-slate-500 text-xs">{fmtDate(c.created_at)}</td>
                  <td className="px-5 py-3 text-center text-slate-600">{c.prizes.length}</td>
                  <td className="px-5 py-3 text-center font-bold text-slate-900">{c.cupo_restante} / {c.cupo_total}</td>
                  <td className="px-5 py-3 text-center"><span className={`text-xs font-bold px-2.5 py-1 rounded-full ${estadoCls(e)}`}>{estadoLabel(e)}</span></td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      <button onClick={() => verGanadores(c)} className="inline-flex items-center gap-1 border border-slate-300 hover:border-primary hover:text-primary text-slate-600 text-xs font-bold rounded-md px-3 py-2 transition-colors">Ver ganadores</button>
                      {e !== "agotada" && (
                        c.active ? (
                          <button onClick={() => desactivar(c.id)} disabled={busyId === c.id} className="inline-flex items-center gap-1 border border-amber-300 hover:bg-amber-50 text-amber-700 text-xs font-bold rounded-md px-3 py-2 transition-colors disabled:opacity-50">Desactivar</button>
                        ) : (
                          <button onClick={() => activar(c.id)} disabled={busyId === c.id} className="inline-flex items-center gap-1 bg-primary hover:bg-primary-hover text-white text-xs font-bold rounded-md px-3 py-2 transition-colors disabled:opacity-50">Activar</button>
                        )
                      )}
                      {!c.active && (
                        <button onClick={() => setConfirmDel(c)} aria-label="Eliminar ruleta" className="w-8 h-8 grid place-items-center rounded-md text-red-600 hover:bg-red-50 transition-colors"><Trash2 size={16} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {campaigns.length === 0 && (
          <div className="p-10 text-center">
            <Gift size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-400">Sin ruletas creadas todavía.</p>
          </div>
        )}
      </Card>

      {spinsFor && (
        <div role="dialog" aria-modal="true" aria-label="Ganadores de la ruleta" className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSpinsFor(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-6 animate-fade-up shadow-md">
            <div className="flex justify-between items-start mb-4"><h3 className="font-head text-xl font-extrabold text-slate-900">Ganadores</h3><button onClick={() => setSpinsFor(null)} aria-label="Cerrar" className="text-slate-400 hover:text-slate-900"><X size={20} /></button></div>
            {loadingSpins ? (
              <p className="text-slate-400 text-sm py-6 text-center">Cargando...</p>
            ) : spins.length === 0 ? (
              <p className="text-slate-400 text-sm py-6 text-center">Nadie ha girado esta ruleta todavía.</p>
            ) : (
              <div className="space-y-2">
                {spins.map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-3 border border-slate-200 rounded-lg px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{s.user_name}</p>
                      <p className="text-xs text-slate-500 truncate">{s.prize_label} {s.sin_stock && <span className="text-red-600 font-semibold">-- sin stock, entregar a mano</span>}</p>
                    </div>
                    <span className="shrink-0 text-xs text-slate-400">{fmtDate(s.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {confirmDel && (
        <ConfirmDialog title="¿Eliminar esta ruleta?" message="Se borra su configuración y su historial de ganadores. Esta acción no se puede deshacer."
          busy={busyId === confirmDel?.id} onConfirm={confirmDelete} onCancel={() => setConfirmDel(null)} />
      )}
    </div>
  );
}

/* Invitaciones por correo (Canva, Duolingo, Gemini, etc.) */
function CanvaQueue() {
  const [items, setItems] = useState(null);
  const [busyGroup, setBusyGroup] = useState(null);
  const load = () => api.get("/admin/canva-queue").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  if (!items) return <Loading />;
  const pendientes = items.filter((i) => i.status === "pendiente");
  // Agrupado por producto -- distintas plataformas (Canva, Duolingo, Gemini,
  // etc.) comparten esta misma cola, así que "marcar como invitados" tiene
  // que ser POR PRODUCTO. Si fuera un solo botón global, marcar los de Canva
  // que ya pegaste también marcaría de paso los de Duolingo que todavía no
  // has tocado -- ahí es donde de verdad importa esta separación.
  const grupos = pendientes.reduce((acc, i) => {
    (acc[i.product_name] = acc[i.product_name] || []).push(i);
    return acc;
  }, {});
  const marcarGrupo = async (productName, ids) => {
    setBusyGroup(productName);
    try {
      const { data } = await api.post("/admin/canva-queue/mark-invited", { ids });
      toast.success(`${data.marcados} marcado${data.marcados === 1 ? "" : "s"} como invitado${data.marcados === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusyGroup(null); }
  };
  return (
    <div>
      <Title icon={Mail}>Invitaciones por Correo</Title>
      <p className="text-sm text-slate-500 -mt-4 mb-6">
        Para productos que se entregan invitando al correo del cliente en la plataforma real (Canva, Duolingo, Gemini, etc.)
        en vez de darle una cuenta ya armada. Entra al panel de esa plataforma, agrega los correos de un grupo, y luego
        dale "Marcar como invitados" a ESE grupo -- cada plataforma es un panel distinto, así que van separadas por producto.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <Card className="p-5">
          <div className="flex items-center gap-2 text-slate-500 mb-1"><Clock size={14} className="text-primary" /><p className="text-[11px] font-semibold uppercase tracking-wide">Pendientes</p></div>
          <p className="font-head text-2xl font-extrabold text-amber-500">{pendientes.length}</p>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-2 text-slate-500 mb-1"><CheckCircle2 size={14} className="text-primary" /><p className="text-[11px] font-semibold uppercase tracking-wide">Invitados</p></div>
          <p className="font-head text-2xl font-extrabold text-emerald-600">{items.length - pendientes.length}</p>
        </Card>
      </div>
      {Object.entries(grupos).map(([productName, grupo]) => (
        <Card key={productName} className="p-5 mb-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">{productName} -- {grupo.length} correo{grupo.length === 1 ? "" : "s"} pendiente{grupo.length === 1 ? "" : "s"}</p>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3 text-sm font-mono break-all">
            {grupo.map((i) => i.email).join(", ")}
          </div>
          <div className="flex gap-3">
            <button onClick={() => copyText(grupo.map((i) => i.email).join(", "))} className="flex-1 flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-white font-bold rounded-lg py-3 transition-colors"><Copy size={16} /> Copiar correos</button>
            <button onClick={() => marcarGrupo(productName, grupo.map((i) => i.id))} disabled={busyGroup === productName} className="flex-1 flex items-center justify-center gap-2 border border-emerald-300 hover:bg-emerald-50 text-emerald-700 font-bold rounded-lg py-3 transition-colors disabled:opacity-50"><CheckCircle2 size={16} /> {busyGroup === productName ? "..." : "Marcar como invitados"}</button>
          </div>
        </Card>
      ))}
      <Card className="overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr><th className="text-left px-5 py-3">Correo</th><th className="text-left px-5 py-3">Producto</th><th className="text-left px-5 py-3">Cliente</th><th className="text-left px-5 py-3">Fecha</th><th className="text-center px-5 py-3">Estado</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((i) => (
              <tr key={i.id} className="hover:bg-slate-50">
                <td className="px-5 py-3 font-mono text-slate-900">{i.email}</td>
                <td className="px-5 py-3 text-slate-600">{i.product_name}</td>
                <td className="px-5 py-3 text-slate-600">{i.user_name}</td>
                <td className="px-5 py-3 text-slate-400 text-xs whitespace-nowrap">{fmtDate(i.created_at)}</td>
                <td className="px-5 py-3 text-center"><span className={`text-xs font-bold px-2.5 py-1 rounded-full ${i.status === "pendiente" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{i.status === "pendiente" ? "Pendiente" : "Invitado"}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="text-slate-400 p-8 text-center">Sin invitaciones por correo todavía.</p>}
      </Card>
    </div>
  );
}

/* Ventas */
const VENTA_FILTER_FIELDS = [
  { v: "cuenta", l: "Cuenta" },
  { v: "usuario", l: "Usuario" },
  { v: "servicio", l: "Servicio" },
  { v: "fecha", l: "Fecha (YYYY-MM-DD)" },
];
function Ventas() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [field, setField] = useState("cuenta");
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // La búsqueda se manda al backend (filtra contra TODA la base) en vez de
  // filtrar en el navegador sobre las ~1000 ventas más recientes que trae la
  // carga inicial -- si no, una venta más vieja que eso no aparecía ni
  // buscándola por usuario/cuenta/servicio/fecha.
  const load = (params) => api.get("/admin/sales", { params }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const buscar = () => load(q.trim() ? { q: q.trim(), field } : undefined);
  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/sales/${confirmDel.sale_id}`);
      toast.success("Venta eliminada");
      setConfirmDel(null);
      load(q.trim() ? { q: q.trim(), field } : undefined);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };
  const mostrarTodas = () => { setQ(""); setField("cuenta"); load(); };
  const filtered = rows;
  return (
    <div>
      <Title icon={ShoppingCart}>Historial de Ventas</Title>
      <Card className="p-6 mb-6">
        <CardHead icon={Search}>Buscar Ventas</CardHead>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && buscar()} placeholder="Buscar por usuario, servicio, cuenta o fecha..."
              className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
          </div>
          <select value={field} onChange={(e) => setField(e.target.value)} className="border border-slate-200 rounded-lg px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-red-500">
            {VENTA_FILTER_FIELDS.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
          </select>
          <button onClick={buscar} className="flex items-center justify-center gap-1.5 bg-primary hover:bg-primary-hover text-white font-bold rounded-lg px-5 py-3 transition-colors"><Search size={15} /> Buscar</button>
          <button onClick={mostrarTodas} className="flex items-center justify-center gap-1.5 bg-slate-700 hover:bg-slate-800 text-white font-bold rounded-lg px-5 py-3 transition-colors"><RotateCcw size={15} /> Mostrar todas</button>
          <button onClick={() => downloadCsv("/admin/sales/export", "ventas.csv")} className="flex items-center justify-center gap-1.5 border border-slate-300 hover:border-primary hover:text-primary text-slate-600 font-bold rounded-lg px-5 py-3 transition-colors"><FileDown size={15} /> Exportar CSV</button>
        </div>
      </Card>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm min-w-[850px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-5 py-3">ID</th>
              <th className="text-left px-5 py-3">Usuario</th>
              <th className="text-left px-5 py-3">Servicio</th>
              <th className="text-left px-5 py-3">Cuenta</th>
              <th className="text-center px-5 py-3">Duración</th>
              <th className="text-right px-5 py-3">Precio</th>
              <th className="text-right px-5 py-3">Fecha de Compra</th>
              <th className="text-right px-5 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50">
                <td className="px-5 py-3 text-slate-400">{r.display_id}</td>
                <td className="px-5 py-3 font-semibold text-slate-900">{r.user_name}</td>
                <td className="px-5 py-3 text-slate-700">{r.product_name}</td>
                <td className="px-5 py-3 text-blue-600"><Secret value={r.cuenta} /></td>
                <td className="px-5 py-3 text-center text-slate-600">{r.duration}</td>
                <td className="px-5 py-3 text-right font-bold text-primary">{money(r.price)}</td>
                <td className="px-5 py-3 text-right text-slate-400 text-xs">{fmtDate(r.created_at)}</td>
                <td className="px-5 py-3 text-right">
                  {r.manual && (
                    <button onClick={() => setConfirmDel(r)} aria-label="Eliminar venta" className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-xs font-bold"><Trash2 size={13} /> Eliminar</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-slate-400 p-8 text-center">Sin ventas.</p>}
      </Card>
      {confirmDel && (
        <ConfirmDialog title="¿Eliminar esta venta?"
          message={`"${confirmDel.product_name}" de ${confirmDel.user_name} por ${money(confirmDel.price)} se eliminará permanentemente. Si esta venta manual tenía más de una cuenta entregada, se borran todas juntas (es una sola venta). No se restaura el stock ni se toca su crédito.`}
          busy={deleting} onConfirm={confirmDelete} onCancel={() => setConfirmDel(null)} />
      )}
    </div>
  );
}

/* Estadísticas */
const STATUS_GOOD = "#0ca30c";
const STATUS_CRITICAL = "#d03b3b";
const pctDelta = (cur, prev) => (!prev ? null : Math.round(((cur - prev) / prev) * 1000) / 10);
function Delta({ value }) {
  if (value === null || value === undefined) return <span className="text-xs font-semibold text-slate-400">Sin comparativa</span>;
  const up = value >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className="inline-flex items-center gap-1 text-xs font-bold" style={{ color: up ? STATUS_GOOD : STATUS_CRITICAL }}>
      <Icon size={13} /> {up ? "+" : ""}{value}%
    </span>
  );
}
function Sparkline({ values, color = "#DC2626" }) {
  if (!values || values.length < 2) return null;
  const w = 96, h = 28, pad = 3;
  const min = Math.min(...values), max = Math.max(...values);
  const range = max - min || 1;
  const pts = values.map((v, i) => [
    pad + (i / (values.length - 1)) * (w - pad * 2),
    h - pad - ((v - min) / range) * (h - pad * 2),
  ]);
  const path = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const [lx, ly] = pts[pts.length - 1];
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0">
      <path d={path} fill="none" stroke="#cbd5e1" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lx} cy={ly} r={2.5} fill={color} />
    </svg>
  );
}
function KpiTile({ label, value, delta, icon: Icon, series, accent }) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        <Icon size={16} className="text-primary" />
      </div>
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="font-head text-2xl font-extrabold text-slate-900">{value}</p>
          <div className="mt-2 flex items-center gap-1.5">
            <Delta value={delta} />
            {delta !== null && delta !== undefined && <span className="text-xs text-slate-400">vs mes anterior</span>}
          </div>
        </div>
        <Sparkline values={series} color={accent} />
      </div>
    </Card>
  );
}
const REC_STYLE = {
  tendencia_alza: { icon: TrendingUp, color: "text-emerald-700 bg-emerald-50" },
  tendencia_baja: { icon: TrendingDown, color: "text-amber-700 bg-amber-50" },
  problema_reportes: { icon: AlertTriangle, color: "text-red-700 bg-red-50" },
  stock_bajo: { icon: Package, color: "text-amber-700 bg-amber-50" },
  sin_stock: { icon: XCircle, color: "text-red-700 bg-red-50" },
  sin_movimiento: { icon: Clock, color: "text-slate-600 bg-slate-100" },
};
function Estadisticas() {
  const { tenant } = useTenant();
  const accent = tenant.primary_color || "#DC2626";
  const [d, setD] = useState(null);
  const [rec, setRec] = useState(null);
  useEffect(() => { api.get("/admin/statistics").then((r) => setD(r.data)); }, []);
  useEffect(() => { api.get("/admin/statistics/recommendations").then((r) => setRec(r.data.recomendaciones)); }, []);
  if (!d) return <Loading />;
  const maxVentas = d.top_products[0]?.ventas || 1;
  return (
    <div>
      <Title icon={BarChart3}>Estadisticas</Title>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <KpiTile label="Ingresos del mes" value={money(d.kpis.ingresos.value)} delta={d.kpis.ingresos.delta} icon={DollarSign} series={d.por_mes.map((m) => m.ingresos)} accent={accent} />
        <KpiTile label="Ventas del mes" value={d.kpis.ventas.value} delta={d.kpis.ventas.delta} icon={ShoppingCart} series={d.por_mes.map((m) => m.ventas)} accent={accent} />
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Top 3 clientes del mes</p>
            <Users size={16} className="text-primary" />
          </div>
          {d.top_clientes.length === 0 ? (
            <p className="text-xs text-slate-400">Sin compras este mes.</p>
          ) : (
            <div className="space-y-2">
              {d.top_clientes.map((c, i) => (
                <div key={i} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-5 h-5 shrink-0 rounded-full grid place-items-center text-[10px] font-bold text-white ${i === 0 ? "bg-amber-500" : i === 1 ? "bg-slate-400" : "bg-amber-700"}`}>{i + 1}</span>
                    <span className="text-sm font-semibold text-slate-700 truncate">{c.name}</span>
                  </div>
                  <span className="text-sm font-bold text-slate-900 shrink-0">{money(c.total)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-6 mb-6">
        <h3 className="font-head font-bold text-slate-900 mb-1 flex items-center gap-2"><Zap size={16} className="text-primary" /> Recomendaciones</h3>
        <p className="text-xs text-slate-400 mb-4">Basado en tus ventas, reportes y stock reales — patrones detectados, no predicciones.</p>
        {rec === null ? (
          <p className="text-xs text-slate-400">Cargando...</p>
        ) : rec.length === 0 ? (
          <p className="text-xs text-slate-400">Sin recomendaciones por ahora — todo se ve estable.</p>
        ) : (
          <div className="space-y-2">
            {rec.map((r, i) => {
              const style = REC_STYLE[r.tipo] || REC_STYLE.sin_movimiento;
              const Icon = style.icon;
              return (
                <div key={i} className={`flex items-start gap-3 rounded-lg p-3 ${style.color}`}>
                  <Icon size={16} className="shrink-0 mt-0.5" />
                  <p className="text-sm font-medium">{r.mensaje}</p>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="p-6 lg:col-span-2">
          <h3 className="font-head font-bold text-slate-900">Ingresos por mes</h3>
          <p className="text-xs text-slate-400 mb-4">Últimos 6 meses</p>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={d.por_mes} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="ingresosFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={accent} stopOpacity={0.12} />
                  <stop offset="100%" stopColor={accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="mes" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={{ stroke: "#cbd5e1" }} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} width={44}
                tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}K` : v)} />
              <Tooltip formatter={(v) => money(v)} labelFormatter={(l) => l} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }} />
              <Area type="monotone" dataKey="ingresos" stroke={accent} strokeWidth={2} fill="url(#ingresosFill)" dot={{ r: 3, fill: accent, strokeWidth: 0 }} activeDot={{ r: 5 }} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="font-head font-bold text-slate-900 mb-4">Top 5 servicios</h3>
          <div className="space-y-3">
            {d.top_products.map((p, i) => (
              <div key={i}>
                <div className="flex justify-between items-baseline text-sm mb-1 gap-2">
                  <span className="font-semibold text-slate-700 truncate">{p.name}</span>
                  <span className="text-slate-500 font-bold shrink-0">{p.ventas}</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${(p.ventas / maxVentas) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="overflow-x-auto lg:col-span-2">
          <div className="px-4 pt-3 pb-0"><h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500"><CalendarClock size={13} className="text-primary" /> Comparativa Mensual</h3></div>
          <table className="w-full text-xs min-w-[520px]">
            <thead className="text-slate-400 uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-1.5 font-semibold">Mes</th>
                <th className="text-right px-4 py-1.5 font-semibold">Ventas</th>
                <th className="text-right px-4 py-1.5 font-semibold">Ingresos</th>
                <th className="text-right px-4 py-1.5 font-semibold">Clientes</th>
                <th className="text-right px-4 py-1.5 font-semibold">vs anterior</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {d.por_mes.map((m, i) => {
                const prev = d.por_mes[i - 1];
                const delta = prev ? pctDelta(m.ingresos, prev.ingresos) : null;
                return (
                  <tr key={`${m.mes}-${i}`} className="hover:bg-slate-50">
                    <td className="px-4 py-1.5 font-semibold text-slate-900 capitalize">{m.mes}</td>
                    <td className="px-4 py-1.5 text-right text-slate-700">{m.ventas}</td>
                    <td className="px-4 py-1.5 text-right font-bold text-slate-900">{money(m.ingresos)}</td>
                    <td className="px-4 py-1.5 text-right text-slate-700">{m.clientes}</td>
                    <td className="px-4 py-1.5 text-right"><Delta value={delta} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>

        <Card className="p-5">
          <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-4">Resumen del Semestre</h3>
          {(() => {
            const semestreIngresos = d.por_mes.reduce((s, m) => s + m.ingresos, 0);
            const semestreVentas = d.por_mes.reduce((s, m) => s + m.ventas, 0);
            const ticketProm = semestreVentas ? semestreIngresos / semestreVentas : 0;
            const mejorMes = d.por_mes.reduce((best, m) => (m.ingresos > best.ingresos ? m : best), d.por_mes[0]);
            return (
              <div className="space-y-4">
                <div><p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Ticket Promedio</p><p className="font-head text-xl font-extrabold text-slate-900">{money(ticketProm)}</p></div>
                <div><p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Ingresos del Semestre</p><p className="font-head text-xl font-extrabold text-slate-900">{money(semestreIngresos)}</p></div>
                <div><p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Mejor Mes</p><p className="font-head text-xl font-extrabold text-primary capitalize">{mejorMes.mes} <span className="text-sm text-slate-500 font-semibold">({money(mejorMes.ingresos)})</span></p></div>
              </div>
            );
          })()}
        </Card>
      </div>
    </div>
  );
}

/* Reportes */
function Reportes() {
  const [reps, setReps] = useState([]);
  const [repStats, setRepStats] = useState(null);
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("todos");
  const [applied, setApplied] = useState({ q: "", estado: "todos" });
  const [responding, setResponding] = useState(null);
  const [respuesta, setRespuesta] = useState("");
  const [respuestaImagen, setRespuestaImagen] = useState("");
  const [uploadingImg, setUploadingImg] = useState(false);
  const [refundQuote, setRefundQuote] = useState(null); // {tipo, monto, detalle}
  const [refundBusy, setRefundBusy] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [replaceBusy, setReplaceBusy] = useState(false);
  const [replaceCreds, setReplaceCreds] = useState({ email: "", password: "", perfil: "" });
  const [lightboxImg, setLightboxImg] = useState(null);
  const load = () => {
    api.get("/admin/reports").then((r) => setReps(r.data));
    api.get("/admin/reports/stats").then((r) => setRepStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEscapeClose(!!responding, () => setResponding(null));
  useEscapeClose(!!lightboxImg, () => setLightboxImg(null));
  const setStatus = async (id, status) => { await api.post(`/admin/reports/${id}/status`, { status }); toast.success(`Marcado como ${status}`); load(); };
  const uploadRespuestaImg = async (f) => {
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) {
      return toast.error("Solo se aceptan imágenes PNG, JPG o WEBP");
    }
    if (f.size > MAX_LOGO_BYTES) {
      return toast.error("La imagen debe pesar menos de 10 MB");
    }
    const fd = new FormData(); fd.append("file", f);
    setUploadingImg(true);
    try {
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRespuestaImagen(data.url);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploadingImg(false);
    }
  };
  const onRespuestaImg = (e) => uploadRespuestaImg(e.target.files?.[0]);
  const respuestaImgInputRef = useRef(null);
  // En el textarea (siempre un destino válido de pegado, a diferencia de un
  // <input type=file> con foco, que en Chrome abre el explorador con Ctrl+V
  // en vez de pegar) -- si lo que se pegó es una imagen, se sube sola; si es
  // texto, se deja que el pegado normal siga su curso. Revisa tanto
  // clipboardData.files (copiar un archivo de imagen desde el Explorador)
  // como .items (copiar una captura/imagen desde una página o app), porque
  // según de dónde se copió, solo uno de los dos trae el archivo real.
  const onRespuestaPaste = (e) => {
    const cd = e.clipboardData;
    if (!cd) return;
    let file = cd.files && cd.files.length > 0 ? Array.from(cd.files).find((f) => f.type.startsWith("image/")) : null;
    if (!file) {
      const item = Array.from(cd.items || []).find((it) => it.type.startsWith("image/"));
      file = item ? item.getAsFile() : null;
    }
    if (file) {
      e.preventDefault();
      uploadRespuestaImg(file);
      return;
    }
    // Se pegó algo con archivos pero no se pudo leer como imagen -- avisar
    // en vez de quedarse en silencio; el pegado de texto normal no entra aquí.
    if (Array.from(cd.types || []).includes("Files")) {
      toast.error("No se pudo leer la imagen del portapapeles. Prueba con \"Seleccionar archivo\".");
    }
  };
  const sendResponse = async () => {
    if (!respuesta.trim()) return toast.error("Escribe una respuesta");
    await api.post(`/admin/reports/${responding.id}/respond`, { respuesta, imagen_url: respuestaImagen || null });
    toast.success("Respuesta enviada al cliente"); setResponding(null); setRespuesta(""); setRespuestaImagen(""); load();
  };
  const askRefund = async (tipo) => {
    try {
      const { data } = await api.get(`/admin/reports/${responding.id}/refund-quote`, { params: { tipo } });
      setRefundQuote({ tipo, ...data });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const confirmRefund = async () => {
    setRefundBusy(true);
    try {
      const { data } = await api.post(`/admin/reports/${responding.id}/refund`, { tipo: refundQuote.tipo });
      toast.success(`Reembolso de ${money(data.monto)} enviado a ${responding.user_name}` + (data.otros_resueltos > 0 ? ` — y ${data.otros_resueltos} reporte${data.otros_resueltos === 1 ? "" : "s"} más de esta misma cuenta se resolvió${data.otros_resueltos === 1 ? "" : "n"} solo${data.otros_resueltos === 1 ? "" : "s"}` : ""));
      setRefundQuote(null); setResponding(null); setRespuesta(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setRefundBusy(false); }
  };
  const confirmReplaceAction = async () => {
    setReplaceBusy(true);
    try {
      const { data } = await api.post(`/admin/reports/${responding.id}/replace`, {
        email: replaceCreds.email.trim() || null,
        password: replaceCreds.password.trim() || null,
        perfil: replaceCreds.perfil.trim() || null,
      });
      toast.success(`Cuenta repuesta para ${responding.user_name}` + (data.otros_resueltos > 0 ? ` — y ${data.otros_resueltos} reporte${data.otros_resueltos === 1 ? "" : "s"} más de esta misma cuenta se resolvió${data.otros_resueltos === 1 ? "" : "n"} solo${data.otros_resueltos === 1 ? "" : "s"}` : ""));
      setConfirmReplace(false); setResponding(null); setRespuesta(""); setReplaceCreds({ email: "", password: "", perfil: "" }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setReplaceBusy(false); }
  };
  const counts = repStats || {
    total: reps.length,
    pendiente: reps.filter((r) => r.status === "pendiente").length,
    proceso: reps.filter((r) => r.status === "en proceso").length,
    resuelto: reps.filter((r) => r.status === "resuelto").length,
  };
  const badge = (s) => s === "resuelto" ? "bg-emerald-100 text-emerald-700" : s === "en proceso" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700";
  const filtered = reps.filter((r) => {
    const okQ = !applied.q || (r.correo_cuenta || "").toLowerCase().includes(applied.q.toLowerCase()) || (r.user_name || "").toLowerCase().includes(applied.q.toLowerCase());
    const okE = applied.estado === "todos" || r.status === applied.estado;
    return okQ && okE;
  });
  const stats = [
    { l: "Total de Reportes", v: counts.total, c: "border-t-primary text-primary" },
    { l: "Pendientes", v: counts.pendiente, c: "border-t-amber-500 text-amber-500" },
    { l: "En Proceso", v: counts.proceso, c: "border-t-blue-500 text-blue-500" },
    { l: "Resueltos", v: counts.resuelto, c: "border-t-emerald-500 text-emerald-600" },
  ];
  return (
    <div>
      <Title icon={AlertTriangle}>Reportes de Clientes</Title>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((s, i) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
            <p className="text-slate-600 font-semibold">{s.l}</p>
            <p className={`font-head text-4xl font-extrabold mt-2 ${s.c.split(" ")[1]}`}>{s.v}</p>
          </div>
        ))}
      </div>
      <Card className="p-5 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <label className="block flex-1 min-w-[200px]"><span className="text-sm font-semibold text-slate-700 mb-1.5 block">Buscar por correo:</span><input data-testid="rep-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && setApplied({ q, estado })} placeholder="Buscar por correo electrónico" className={inputCls()} /></label>
          <label className="block"><span className="text-sm font-semibold text-slate-700 mb-1.5 block">Filtrar por estado:</span>
            <select data-testid="rep-filter-status" value={estado} onChange={(e) => setEstado(e.target.value)} className={inputCls()}>
              <option value="todos">Todos</option><option value="pendiente">Pendiente</option><option value="en proceso">En Proceso</option><option value="resuelto">Resuelto</option>
            </select>
          </label>
          <button data-testid="rep-buscar" onClick={() => setApplied({ q, estado })} className="flex items-center gap-2 bg-primary hover:bg-primary-hover text-white font-semibold rounded-lg px-5 py-3 transition-colors"><Search size={16} /> Buscar</button>
          <button data-testid="rep-limpiar" onClick={() => { setQ(""); setEstado("todos"); setApplied({ q: "", estado: "todos" }); }} className="flex items-center gap-2 bg-slate-500 hover:bg-slate-600 text-white font-semibold rounded-lg px-5 py-3 transition-colors"><X size={16} /> Limpiar</button>
          <button onClick={() => downloadCsv("/admin/reports/export", "reportes.csv")} className="flex items-center gap-2 border border-slate-300 hover:border-primary hover:text-primary text-slate-600 font-semibold rounded-lg px-5 py-3 transition-colors"><FileDown size={16} /> Exportar CSV</button>
        </div>
      </Card>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="text-slate-500 text-xs uppercase tracking-wide border-b border-slate-100"><tr>
            <th className="text-left px-5 py-4">Cliente</th><th className="text-left px-5 py-4">Plataforma</th><th className="text-left px-5 py-4">Cuenta</th><th className="text-left px-5 py-4">Problema</th><th className="text-left px-5 py-4">Fechas</th><th className="text-left px-5 py-4">Estado</th><th className="text-left px-5 py-4">Respuesta Admin</th><th className="text-right px-5 py-4">Acciones</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`report-row-${r.id}`} className="hover:bg-slate-50 align-top">
                <td className="px-5 py-4 font-bold text-slate-900">{r.user_name}</td>
                <td className="px-5 py-4"><span className="inline-block bg-primary text-white text-[11px] font-bold px-2.5 py-1 rounded uppercase">{r.plataforma || r.subject}</span></td>
                <td className="px-5 py-4 text-slate-600 text-xs">
                  {r.correo_cuenta && (
                    <p className="flex items-center gap-1"><b>Email:</b> {r.correo_cuenta}
                      <button onClick={() => copyText(r.correo_cuenta)} aria-label="Copiar correo" className="w-5 h-5 shrink-0 grid place-items-center rounded bg-primary hover:bg-primary-hover text-white transition-colors"><Copy size={10} /></button>
                    </p>
                  )}
                  {r.password_cuenta && (
                    <p className="flex items-center gap-1 mt-0.5"><b>Contraseña:</b> <Secret value={r.password_cuenta} />
                      <button onClick={() => copyText(r.password_cuenta)} aria-label="Copiar contraseña" className="w-5 h-5 shrink-0 grid place-items-center rounded bg-primary hover:bg-primary-hover text-white transition-colors"><Copy size={10} /></button>
                    </p>
                  )}
                  {r.perfil && <p className="mt-0.5"><b>Perfil:</b> {r.perfil}</p>}
                </td>
                <td className="px-5 py-4 text-slate-600 max-w-[220px]">
                  {r.descripcion || r.message}
                  {r.imagen_url && (
                    <button type="button" onClick={() => setLightboxImg(r.imagen_url)} className="block mt-1.5">
                      <img src={r.imagen_url} alt="Foto del cliente" className="w-14 h-14 rounded object-cover border border-slate-200" />
                    </button>
                  )}
                </td>
                <td className="px-5 py-4 text-xs text-slate-500">{r.fecha_compra && <p><b>Compra:</b> {fmtDateOnly(r.fecha_compra)}</p>}<p><b>Reportado:</b> {fmtDate(r.created_at)}</p>{r.respondido_at && <p><b>Respondido:</b> {fmtDate(r.respondido_at)}</p>}</td>
                <td className="px-5 py-4"><span className={`text-xs font-bold px-2 py-1 rounded uppercase ${badge(r.status)}`}>{r.status}</span></td>
                <td className="px-5 py-4 text-xs max-w-[220px]">
                  {r.respuesta_admin ? (
                    <div className="bg-slate-900 text-white rounded-lg p-3">
                      <p className="flex items-center gap-1 text-emerald-400 font-semibold mb-1">↩ Respuesta:</p>
                      <p className="text-slate-100 break-words">{r.respuesta_admin}</p>
                      {r.respuesta_imagen && (
                        <button type="button" onClick={() => setLightboxImg(r.respuesta_imagen)} className="block mt-1.5">
                          <img src={r.respuesta_imagen} alt="Adjunto de la respuesta" className="w-14 h-14 rounded object-cover" />
                        </button>
                      )}
                      {r.respondido_por && <p className="text-slate-400 mt-1.5">Respondió: {r.respondido_por}</p>}
                    </div>
                  ) : <span className="text-slate-400 italic">— Sin respuesta</span>}
                </td>
                <td className="px-5 py-4">
                  {r.status !== "resuelto" && (
                    <div className="flex flex-col gap-2 items-end">
                      <button data-testid={`respond-${r.id}`} onClick={() => { setResponding(r); setRespuesta(r.respuesta_admin || ""); setRespuestaImagen(r.respuesta_imagen || ""); }} className="w-full flex items-center justify-center gap-1.5 bg-primary hover:bg-primary-hover text-white text-xs font-bold rounded-md px-3 py-2 transition-colors">↩ Responder</button>
                      {r.status !== "en proceso" && <button data-testid={`proceso-${r.id}`} onClick={() => setStatus(r.id, "en proceso")} className="w-full flex items-center justify-center gap-1.5 border border-slate-300 hover:border-primary hover:text-primary text-slate-600 text-xs font-bold rounded-md px-3 py-2 transition-colors">⚙ En Proceso</button>}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-slate-400 p-8 text-center">No hay reportes que coincidan.</p>}
      </Card>
      {responding && (
        <div role="dialog" aria-modal="true" aria-label="Responder reporte" className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setResponding(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6 animate-fade-up shadow-md">
            <div className="flex justify-between items-start mb-2"><h3 className="font-head text-xl font-extrabold text-slate-900">Responder reporte</h3><button onClick={() => setResponding(null)} aria-label="Cerrar" className="text-slate-400 hover:text-slate-900"><X size={20} /></button></div>
            <p className="text-sm text-slate-500">{responding.user_name} · {responding.plataforma || responding.subject}</p>
            <p className="text-sm text-slate-600 mt-2 bg-slate-50 rounded-lg p-3">{responding.descripcion || responding.message}</p>
            <textarea data-testid="respuesta-text" value={respuesta} onChange={(e) => setRespuesta(e.target.value)} onPaste={onRespuestaPaste} rows={4} placeholder="Escribe la respuesta / solución para el cliente..." className={`mt-4 ${inputCls()}`} />
            <button type="button" data-testid="respuesta-reemplazo" onClick={() => { setReplaceCreds({ email: "", password: "", perfil: "" }); setConfirmReplace(true); }} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-primary border border-slate-200 hover:border-primary rounded-md px-3 py-1.5 transition-colors"><Package size={13} /> Reponer cuenta</button>
            <div className="mt-3">
              <span className="text-sm font-semibold text-slate-700 mb-1.5 block">Adjuntar foto (opcional):</span>
              {respuestaImagen ? (
                <div className="flex items-center gap-3">
                  <img src={respuestaImagen} alt="Adjunto de la respuesta" className="w-16 h-16 rounded-lg object-cover border border-slate-200" />
                  <button onClick={() => setRespuestaImagen("")} className="text-xs font-semibold text-red-600 hover:text-red-700 inline-flex items-center gap-1"><Trash2 size={12} /> Quitar</button>
                </div>
              ) : (
                <>
                  <input ref={respuestaImgInputRef} data-testid="respuesta-img" type="file" accept="image/*" onChange={onRespuestaImg} disabled={uploadingImg} className="hidden" tabIndex={-1} />
                  <button type="button" onClick={() => respuestaImgInputRef.current?.click()} disabled={uploadingImg} className="inline-flex items-center gap-1.5 bg-primary hover:bg-primary-hover disabled:opacity-50 text-white text-sm font-semibold rounded-md px-4 py-2 transition-colors">Seleccionar archivo</button>
                  <p className="text-xs text-slate-400 mt-1.5">O copia una captura de pantalla y pégala con Ctrl+V dentro del recuadro de respuesta de arriba</p>
                </>
              )}
              {uploadingImg && <p className="text-xs text-slate-400 mt-1">Subiendo imagen...</p>}
            </div>
            <button data-testid="respuesta-send" onClick={sendResponse} disabled={uploadingImg} className="mt-4 w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover disabled:opacity-60 text-white font-bold rounded-lg py-3 transition-colors"><Send size={17} /> Enviar respuesta y resolver</button>

            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mt-5 mb-2">O resolver con reembolso (según el precio de {responding.plataforma || "este producto"}):</p>
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="respuesta-refund-proporcional" onClick={() => askRefund("proporcional")} className="flex items-center justify-center gap-1.5 border border-emerald-300 hover:bg-emerald-50 text-emerald-700 text-sm font-bold rounded-lg py-2.5 transition-colors">
                <Wallet size={15} /> Reembolso proporcional
              </button>
              <button data-testid="respuesta-refund-total" onClick={() => askRefund("total")} className="flex items-center justify-center gap-1.5 border border-emerald-300 hover:bg-emerald-50 text-emerald-700 text-sm font-bold rounded-lg py-2.5 transition-colors">
                <Wallet size={15} /> Saldo total
              </button>
            </div>
          </div>
        </div>
      )}
      {refundQuote && (
        <ConfirmDialog
          title="Confirmar reembolso"
          message={`${refundQuote.detalle}. Se acreditarán ${money(refundQuote.monto)} a ${responding.user_name} y el reporte quedará resuelto. Si esta cuenta se vuelve a reportar, se aplicará el mismo tipo de reembolso (${refundQuote.tipo === "total" ? "saldo total" : "proporcional a los días sin usar"}) en automático.`}
          confirmLabel={refundBusy ? "Acreditando..." : `Confirmar ${money(refundQuote.monto)}`}
          busy={refundBusy}
          onConfirm={confirmRefund}
          onCancel={() => setRefundQuote(null)}
        />
      )}
      {confirmReplace && (
        <ConfirmDialog
          title="Reponer cuenta"
          message={`Se registrará una entrega de "${responding.plataforma || responding.subject}" en el historial de compras de ${responding.user_name} (sin costo) y el reporte quedará resuelto. Si esta cuenta se vuelve a reportar, se repondrá sola con estos mismos datos.`}
          confirmLabel={replaceBusy ? "Registrando..." : "Confirmar reposición"}
          busy={replaceBusy}
          onConfirm={confirmReplaceAction}
          onCancel={() => { setConfirmReplace(false); setReplaceCreds({ email: "", password: "", perfil: "" }); }}
        >
          <div className="mt-3 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Cuenta nueva (opcional)</p>
            <input data-testid="replace-email" value={replaceCreds.email} onChange={(e) => setReplaceCreds((c) => ({ ...c, email: e.target.value }))} placeholder="Correo de la cuenta nueva" className={inputCls()} />
            <input data-testid="replace-password" value={replaceCreds.password} onChange={(e) => setReplaceCreds((c) => ({ ...c, password: e.target.value }))} placeholder="Contraseña" className={inputCls()} />
            <input data-testid="replace-perfil" value={replaceCreds.perfil} onChange={(e) => setReplaceCreds((c) => ({ ...c, perfil: e.target.value }))} placeholder="Perfil (opcional)" className={inputCls()} />
          </div>
        </ConfirmDialog>
      )}
      {lightboxImg && (
        <div role="dialog" aria-modal="true" aria-label="Foto ampliada" className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setLightboxImg(null)}>
          <div onClick={(e) => e.stopPropagation()} className="relative max-w-md w-full">
            <button onClick={() => setLightboxImg(null)} aria-label="Cerrar" className="absolute -top-3 -right-3 w-8 h-8 grid place-items-center rounded-full bg-white text-slate-700 hover:text-slate-900 shadow-md"><X size={16} /></button>
            <img src={lightboxImg} alt="Foto ampliada" className="w-full max-h-[75vh] object-contain rounded-xl bg-white" />
          </div>
        </div>
      )}
    </div>
  );
}

/* Códigos de Acceso (Netflix/Disney+/Fox One/Apple TV, etc.) */
function Codigos() {
  const [reqs, setReqs] = useState([]);
  const [resolving, setResolving] = useState(null); // {id, user_name, plataforma, correo_cuenta}
  const [codeInput, setCodeInput] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/codes").then((r) => setReqs(r.data));
  useEffect(() => { load(); }, []);
  useEscapeClose(!!resolving, () => setResolving(null));

  const resolve = async () => {
    if (!/^\d{6}$/.test(codeInput.trim())) return toast.error("El código debe tener 6 dígitos");
    setBusy(true);
    try {
      await api.post(`/admin/codes/${resolving.id}/resolve`, { code: codeInput.trim() });
      toast.success(`Código enviado a ${resolving.user_name}`);
      setResolving(null); setCodeInput(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const pendientes = reqs.filter((r) => r.status === "pendiente");
  const resueltos = reqs.filter((r) => r.status === "resuelto");
  const badge = (s) => s === "resuelto" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700";

  return (
    <div>
      <Title icon={KeyRound}>Códigos de Acceso</Title>
      <p className="text-slate-500 mb-6 -mt-4">Solicitudes de código de verificación (Netflix, Disney+, Fox One, Apple TV, etc.) — también puedes responderlas directo en Telegram respondiendo el aviso con el código de 6 dígitos.</p>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
          <p className="text-slate-600 font-semibold">Pendientes</p>
          <p className="font-head text-4xl font-extrabold mt-2 text-amber-500">{pendientes.length}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
          <p className="text-slate-600 font-semibold">Resueltos</p>
          <p className="font-head text-4xl font-extrabold mt-2 text-emerald-600">{resueltos.length}</p>
        </div>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="text-slate-500 text-xs uppercase tracking-wide border-b border-slate-100"><tr>
            <th className="text-left px-5 py-4">Cliente</th><th className="text-left px-5 py-4">Plataforma</th><th className="text-left px-5 py-4">Correo</th><th className="text-left px-5 py-4">Fecha</th><th className="text-left px-5 py-4">Estado</th><th className="text-right px-5 py-4">Acciones</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {reqs.map((r) => (
              <tr key={r.id} data-testid={`code-row-${r.id}`} className="hover:bg-slate-50 align-top">
                <td className="px-5 py-4 font-bold text-slate-900">{r.user_name}</td>
                <td className="px-5 py-4"><span className="inline-block bg-primary text-white text-[11px] font-bold px-2.5 py-1 rounded uppercase">{r.plataforma || "—"}</span></td>
                <td className="px-5 py-4 text-slate-600 flex items-center gap-1">{r.correo_cuenta}
                  <button onClick={() => copyText(r.correo_cuenta)} aria-label="Copiar correo" className="w-5 h-5 shrink-0 grid place-items-center rounded bg-primary hover:bg-primary-hover text-white transition-colors"><Copy size={10} /></button>
                </td>
                <td className="px-5 py-4 text-xs text-slate-500">{fmtDate(r.created_at)}</td>
                <td className="px-5 py-4"><span className={`text-xs font-bold px-2 py-1 rounded uppercase ${badge(r.status)}`}>{r.status}</span>{r.status === "resuelto" && r.code && <p className="mt-1 font-head font-bold text-slate-900 tracking-wider">{r.code}</p>}</td>
                <td className="px-5 py-4 text-right">
                  {r.status !== "resuelto" && (
                    <button data-testid={`resolve-${r.id}`} onClick={() => { setResolving(r); setCodeInput(""); }} className="flex items-center gap-1.5 bg-primary hover:bg-primary-hover text-white text-xs font-bold rounded-md px-3 py-2 transition-colors ml-auto"><KeyRound size={12} /> Resolver</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {reqs.length === 0 && <p className="text-center text-slate-400 py-10">Sin solicitudes todavía.</p>}
      </Card>

      {resolving && (
        <div role="dialog" aria-modal="true" aria-label="Resolver código" className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setResolving(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-sm w-full p-6" >
            <h3 className="font-head text-lg font-bold text-slate-900 mb-1">Enviar código a {resolving.user_name}</h3>
            <p className="text-sm text-slate-500 mb-4">{resolving.plataforma || "Cuenta"} · {resolving.correo_cuenta}</p>
            <input data-testid="resolve-code-input" value={codeInput} onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456" inputMode="numeric" maxLength={6} className={inputCls() + " text-center font-head text-2xl tracking-widest"} />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setResolving(null)} className="flex-1 border border-slate-200 hover:border-slate-300 text-slate-600 font-semibold rounded-lg py-2.5 transition-colors">Cancelar</button>
              <button data-testid="resolve-code-confirm" onClick={resolve} disabled={busy} className="flex-1 bg-primary hover:bg-primary-hover disabled:opacity-60 text-white font-bold rounded-lg py-2.5 transition-colors">{busy ? "Enviando..." : "Enviar"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Vencimientos */
function Vencimientos() {
  const [items, setItems] = useState([]);
  const [notified, setNotified] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/expirations").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const key = (it) => `${it.user_id}-${it.product}-${it.expires_at}`;

  const notify = async (it) => {
    await api.post("/admin/notifications", {
      title: "Aviso", type: "alerta", user_id: it.user_id,
      message: `Tu cuenta de ${it.product} vence el ${new Date(it.expires_at).toLocaleDateString("es-MX")}. Renueva a tiempo para no perder el acceso.`,
    });
    setNotified((s) => new Set(s).add(key(it)));
  };
  const notifyOne = async (it) => { await notify(it); toast.success(`Aviso enviado a ${it.user}`); };
  const notifyAllUrgent = async () => {
    const urgent = items.filter((it) => it.days_left >= 0 && it.days_left <= 3 && !notified.has(key(it)));
    if (!urgent.length) return toast.error("No hay vencimientos urgentes sin avisar");
    setBusy(true);
    try { await Promise.all(urgent.map(notify)); toast.success(`Aviso enviado a ${urgent.length} cliente${urgent.length === 1 ? "" : "s"}`); }
    finally { setBusy(false); }
  };

  const urgentes = items.filter((it) => it.days_left >= 0 && it.days_left <= 3).length;
  const proximos = items.filter((it) => it.days_left > 3 && it.days_left <= 7).length;
  const vencidos = items.filter((it) => it.days_left < 0).length;
  const stats = [
    { icon: AlertTriangle, label: "Vencen en 3 días o menos", value: urgentes, color: "text-red-600" },
    { icon: CalendarClock, label: "Vencen en 7 días o menos", value: proximos, color: "text-amber-500" },
    { icon: XCircle, label: "Ya vencidos", value: vencidos, color: "text-slate-500" },
  ];

  return (
    <div>
      <Title icon={CalendarClock}>Vencimientos</Title>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        {stats.map((s) => (
          <Card key={s.label} className="p-5">
            <div className="flex items-center gap-2 text-slate-500 mb-1"><s.icon size={14} className="text-primary" /><p className="text-[11px] font-semibold uppercase tracking-wide">{s.label}</p></div>
            <p className={`font-head text-2xl font-extrabold ${s.color}`}>{s.value}</p>
          </Card>
        ))}
      </div>
      <div className="flex justify-end mb-4">
        <button onClick={notifyAllUrgent} disabled={busy || urgentes === 0} className="flex items-center gap-2 bg-primary hover:bg-primary-hover disabled:bg-slate-200 disabled:text-slate-400 text-white font-bold rounded-lg px-5 py-2.5 text-sm transition-colors">
          <Bell size={15} /> {busy ? "Enviando..." : "Avisar a todos los urgentes"}
        </button>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm min-w-[650px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr><th className="text-left px-5 py-3">Cliente</th><th className="text-left px-5 py-3">Servicio</th><th className="text-left px-5 py-3">Vence</th><th className="text-center px-5 py-3">Estado</th><th className="text-right px-5 py-3">Acciones</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((it, i) => {
              const already = notified.has(key(it));
              return (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-semibold text-slate-900">{it.user}</td>
                  <td className="px-5 py-3 text-slate-600">{it.product}</td>
                  <td className="px-5 py-3 text-slate-500">{new Date(it.expires_at).toLocaleDateString("es-MX")}</td>
                  <td className="px-5 py-3 text-center">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                      it.days_left < 0 ? "bg-slate-200 text-slate-600"
                      : it.days_left <= 3 ? "bg-red-100 text-red-700"
                      : it.days_left <= 7 ? "bg-amber-100 text-amber-700"
                      : "bg-emerald-100 text-emerald-700"
                    }`}>{it.days_left < 0 ? "Vencido" : `${it.days_left} días`}</span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    {it.days_left >= 0 && it.days_left <= 7 && (
                      already ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><CheckCircle2 size={13} /> Avisado</span>
                      ) : (
                        <button onClick={() => notifyOne(it)} className="inline-flex items-center gap-1 border border-slate-300 hover:border-primary hover:text-primary text-slate-600 text-xs font-bold rounded-md px-3 py-1.5 transition-colors"><Bell size={12} /> Notificar</button>
                      )
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {items.length === 0 && <p className="text-slate-400 p-8 text-center">Sin vencimientos.</p>}
      </Card>
    </div>
  );
}

/* Bitacora */
function Bitacora() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/actions").then((r) => setItems(r.data)); }, []);
  if (!items) return <Loading />;
  const filtered = q
    ? items.filter((a) => a.admin_name.toLowerCase().includes(q.toLowerCase()) || a.action.toLowerCase().includes(q.toLowerCase()) || a.detail.toLowerCase().includes(q.toLowerCase()))
    : items;
  return (
    <div>
      <Title icon={History}>Bitácora</Title>
      <p className="text-xs text-slate-500 mb-4">Registro de acciones sensibles hechas por cualquier administrador: créditos, bloqueos, altas/bajas de usuarios, cupones y recargas aprobadas o canceladas.</p>
      <Card className="overflow-x-auto">
        <div className="p-5 pb-4">
          <div className="relative max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por admin, acción o detalle..." className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
          </div>
        </div>
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-5 py-3">Fecha</th>
              <th className="text-left px-5 py-3">Admin</th>
              <th className="text-left px-5 py-3">Acción</th>
              <th className="text-left px-5 py-3">Detalle</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((a) => (
              <tr key={a.id} className="hover:bg-slate-50 align-top">
                <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{fmtDate(a.created_at)}</td>
                <td className="px-5 py-3 font-semibold text-slate-900 whitespace-nowrap">{a.admin_name}</td>
                <td className="px-5 py-3 whitespace-nowrap"><span className="text-xs font-bold uppercase px-2 py-0.5 rounded bg-slate-100 text-slate-700">{a.action}</span></td>
                <td className="px-5 py-3 text-slate-600">{a.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-slate-400 p-8 text-center">Sin registros todavía.</p>}
      </Card>
    </div>
  );
}

/* Notificaciones */
const NOTIF_TITLES = ["Promoción", "Aviso", "Advertencia"];
function Notificaciones() {
  const [notifs, setNotifs] = useState([]);
  const [users, setUsers] = useState([]);
  const [target, setTarget] = useState(""); // user id, "" = todos
  const [targetQ, setTargetQ] = useState("");
  const [title, setTitle] = useState(NOTIF_TITLES[0]);
  const [msg, setMsg] = useState("");
  const load = () => api.get("/admin/notifications").then((r) => setNotifs(r.data));
  useEffect(() => { load(); api.get("/admin/users").then((r) => setUsers(r.data.filter((u) => u.role === "client"))); }, []);
  const send = async () => { if (!title) return toast.error("Escribe un título"); await api.post("/admin/notifications", { title, message: msg, type: "info", user_id: target }); setTitle(NOTIF_TITLES[0]); setMsg(""); setTarget(""); setTargetQ(""); toast.success(target ? "Notificación enviada al usuario" : "Notificación enviada a todos"); load(); };
  const read = async (id) => { await api.post(`/admin/notifications/${id}/read`); load(); };
  const readAll = async () => { const { data } = await api.post("/admin/notifications/read-all"); toast.success(data.marked > 0 ? `${data.marked} notificación${data.marked === 1 ? "" : "es"} marcada${data.marked === 1 ? "" : "s"} como leída${data.marked === 1 ? "" : "s"}` : "No había notificaciones sin leer"); load(); };
  const hayNoLeidas = notifs.some((n) => !n.read);
  const selectedUser = users.find((u) => u.id === target);
  const searchRes = targetQ ? users.filter((u) => u.name.toLowerCase().includes(targetQ.toLowerCase()) || u.username.toLowerCase().includes(targetQ.toLowerCase())) : [];
  return (
    <div>
      <Title icon={Bell}>Notificaciones</Title>
      <Card className="p-6 mb-6">
        <CardHead icon={Send}>Enviar Notificación</CardHead>
        <Field label="Destinatario:">
          {selectedUser ? (
            <div className="flex items-center justify-between gap-3 bg-slate-900 text-white rounded-lg px-4 py-3">
              <span className="text-sm font-semibold truncate">{selectedUser.name} · {selectedUser.email}</span>
              <button onClick={() => { setTarget(""); setTargetQ(""); }} aria-label="Quitar destinatario" className="text-white/70 hover:text-white shrink-0"><X size={16} /></button>
            </div>
          ) : (
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="notif-target-search" value={targetQ} onChange={(e) => setTargetQ(e.target.value)} placeholder="Buscar usuario, o deja en blanco para enviar a todos" className={`pl-10 ${inputCls()}`} />
              {searchRes.length > 0 && (
                <div className="mt-2 border border-slate-200 rounded-lg divide-y max-h-52 overflow-y-auto">
                  {searchRes.map((u) => (
                    <button key={u.id} onClick={() => { setTarget(u.id); setTargetQ(""); }} className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex justify-between">
                      <span className="font-semibold text-sm">{u.name}</span><span className="text-xs text-slate-400">{u.email}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </Field>
        <Field label="Título:">
          <select data-testid="notif-title" value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls()}>
            {NOTIF_TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Mensaje:"><textarea data-testid="notif-message" value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} placeholder="Escribe el mensaje..." className={inputCls()} /></Field>
        <BigBtn testid="notif-send" onClick={send}><Send size={18} /> Enviar Notificación</BigBtn>
      </Card>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Historial ({notifs.length})</p>
        {hayNoLeidas && <button data-testid="notif-read-all" onClick={readAll} className="flex items-center gap-1.5 text-xs font-bold text-primary hover:text-primary-hover"><CheckCircle2 size={14} /> Marcar todas como leídas</button>}
      </div>
      <div className="space-y-2">
        {notifs.map((n) => (
          <Card key={n.id} className={`p-4 flex items-start justify-between gap-4 ${n.read ? "opacity-60" : ""}`}>
            <div className="flex gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary/10 grid place-items-center text-primary shrink-0"><Bell size={16} /></div>
              <div>
                <p className="font-semibold text-slate-900">
                  {n.title}{" "}
                  {n.user_id
                    ? <span className="text-[11px] bg-slate-900 text-white px-1.5 py-0.5 rounded ml-1">privado</span>
                    : <span className="text-[11px] bg-primary/10 text-primary px-1.5 py-0.5 rounded ml-1">todos</span>}
                </p>
                <p className="text-sm text-slate-500">{n.message}</p>
                <p className="text-xs text-slate-400 mt-0.5">{fmtDate(n.created_at)}</p>
              </div>
            </div>
            {!n.read && <button onClick={() => read(n.id)} className="text-xs font-semibold text-primary hover:text-primary-hover shrink-0">Marcar leída</button>}
          </Card>
        ))}
        {notifs.length === 0 && <p className="text-slate-400 text-center py-10">Sin notificaciones enviadas todavía.</p>}
      </div>
    </div>
  );
}
